import React, { createContext, useState, useContext, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  return useContext(LanguageContext);
};

// 支持的語言
export const SUPPORTED_LANGUAGES = {
  'en_US': {
    code: 'en_US',
    name: 'English',
    flag: '🇺🇸'
  },
  'zh_HK': {
    code: 'zh_HK',
    name: '繁體中文',
    flag: '🇭🇰'
  }
};

// 翻譯文本
export const translations = {
  en_US: {
    // 登入頁面
    'login.title': 'Sign in to your account',
    'login.subtitle': 'Access the Nexi Dashboard',
    'login.traditional': 'Traditional Login',
    'login.phone': 'Phone Login',
    'login.username': 'Username',
    'login.usernameOrEmail': 'Username or Email',
    'login.password': 'Password',
    'login.clinicId': 'Clinic ID',
    'login.clinicIdPlaceholder': 'Clinic ID',
    'login.signIn': 'Sign in',
    'login.signingIn': 'Signing in...',
    'login.notRegisteredHint': 'Not yet registered? Use your subscribed phone number to ',
    'login.loginHere': 'login here',

    // 手機登入
    'phoneLogin.phoneNumber': 'Phone Number',
    'phoneLogin.phoneNumberPlaceholder': 'Enter phone number (e.g., +85212345678)',
    'phoneLogin.queryClinics': 'Query Clinics',
    'phoneLogin.querying': 'Querying...',
    'phoneLogin.selectClinic': 'Select Clinic',
    'phoneLogin.back': 'Back',
    'phoneLogin.sendCode': 'Send Verification Code',
    'phoneLogin.sending': 'Sending...',
    'phoneLogin.verificationCode': 'Verification Code',
    'phoneLogin.codeSentTo': 'Verification code sent to',
    'phoneLogin.enterCode': 'Enter 6-digit verification code',
    'phoneLogin.verifyAndLogin': 'Verify and Login',
    'phoneLogin.verifying': 'Verifying...',

    // 錯誤訊息
    'error.phoneRequired': 'Please enter phone number',
    'error.clinicRequired': 'Please select a clinic',
    'error.codeRequired': 'Please enter verification code',
    'error.clinicsNotFound': 'Query clinics failed, please check phone number',
    'error.sendCodeFailed': 'Failed to send verification code',
    'error.codeInvalid': 'Verification code is incorrect or expired',

    // Centralized error codes
    'error.usernameAlreadyExists': 'Username already exists',
    'error.phoneNumberNotAvailable': 'Phone number not available',
    'error.noClinicsFoundForThisPhoneNumber': 'No clinics found for this phone number',
    'error.failedToSendVerificationCode': 'Failed to send verification code',
    'error.passwordTooShort': 'Password must be at least 6 characters long',
    'error.incorrectUsernameOrPassword': 'Incorrect username or password',
    'error.emailAlreadyRegistered': 'Email already registered',
    'error.phoneNumberNotAssociatedWithThisClinic': 'Phone number not associated with this clinic',
    'error.clinicNotFound': 'Clinic not found',
    'error.invalidOrExpiredVerificationCode': 'Invalid or expired verification code',
    'error.currentPasswordIncorrect': 'Current password is incorrect',
    'error.twoFactorAuthOnlyForTempUsers': '2FA password update is only available for temp users',
    'error.phoneNumberNotAvailableForVerification': 'Phone number not available for verification',
    'error.newPasswordTooShort': 'New password must be at least 6 characters long',
    'error.passwordsMustBeDifferent': 'New password must be different from current password',
    'error.twoFactorAuthOnlyForRegularUsers': '2FA username update is only available for regular users',
    'error.newUsernameTooShort': 'New username must be at least 3 characters long',
    'error.usernamesMustBeDifferent': 'New username must be different from current username',
    'error.verificationCodeNotFound': 'Verification code not found or expired',
    'error.invalidVerificationCode': 'Invalid verification code',
    'error.conversionOnlyForTempUsers': 'Account conversion is only available for temp users',
    'error.invalidEmailFormat': 'Invalid email format',
    'error.tempUsersShouldUseTwoFactorAuth': 'Temp users should use 2FA password update endpoints',
    'error.usernameRequired': 'Username is required',
    'error.emailRequired': 'Email is required',

    // 導航
    'nav.dashboard': 'Dashboard',
    'nav.appointment': 'Appointments',
    'nav.blockSchedule': 'Holiday',
    'nav.patients': 'Patients',
    'nav.doctors': 'Doctors',
    'nav.locations': 'Locations',
    'nav.qa': 'Q&A Knowledge Base',
    'nav.voicemail': 'Voicemail',
    'nav.callLogs': 'Call Logs',
    'nav.appointmentReminderJobs': 'Appointment Reminder Jobs',
    'nav.appointmentReminderSettings': 'Appointment Reminder Settings',
    'nav.conversations': 'Conversations',
    'nav.conversationActivities': 'Conversation Activities',
    'nav.media': 'Media',
    'nav.import': 'Data Import',
    'nav.subscriptions': 'Subscriptions',
    'nav.analytics': 'Analytics',
    'nav.toolbox': 'Toolbox',
    'nav.metaWhatsAppTemplates': 'WhatsApp Templates',
    'nav.embeddedSignup': 'Embedded Signup',
    'nav.settings': 'Settings',
    'nav.users': 'Users',
    'nav.signOut': 'Sign out',

    // 儀表板
    'dashboard.title': 'Dashboard',
    'dashboard.welcome': 'Welcome back',

    // 來電記錄
    'callLogs.title': 'Call Logs',
    'callLogs.subtitle': 'View all missed call text back messages',
    'callLogs.search': 'Search call logs...',
    'callLogs.noResults': 'No call logs found',
    'callLogs.loading': 'Loading call logs...',
    'callLogs.error': 'Error loading call logs',
    'callLogs.fromNumber': 'From',
    'callLogs.toNumber': 'To',
    'callLogs.timestamp': 'Time',
    'callLogs.message': 'Message',
    'callLogs.channel': 'Channel',
    'callLogs.customer': 'Customer',
    'callLogs.totalCalls': 'Total Calls',
    'callLogs.todayCalls': 'Today\'s Calls',
    'callLogs.dateFrom': 'From Date',
    'callLogs.dateTo': 'To Date',
    'callLogs.filter': 'Filter',
    'callLogs.clear': 'Clear',
    'callLogs.type': 'Type',
    'callLogs.voicemail': 'Voicemail',
    'callLogs.call': 'Call',
    'callLogs.playVoicemail': 'Play voicemail',
    'callLogs.duration': 'Duration',
    'dashboard.callsToday': 'Calls Today',
    'dashboard.viewCallsToday': 'View today\'s calls',

    // Media
    'media.title': 'Media',
    'media.subtitle': 'View all media that patients attempt to send',
    'media.search': 'Search media...',
    'media.noResults': 'No media found',
    'media.loading': 'Loading media...',
    'media.error': 'Error loading media',
    'media.patientName': 'Patient',
    'media.fromNumber': 'From',
    'media.toNumber': 'To',
    'media.mediaType': 'Type',
    'media.timestamp': 'Time',
    'media.channel': 'Channel',
    'media.status': 'Status',
    'media.actions': 'Actions',
    'media.view': 'View',
    'media.download': 'Download',
    'media.filterByType': 'Filter by type:',
    'media.allTypes': 'All Types',
    'media.image': 'Image',
    'media.audio': 'Audio',
    'media.video': 'Video',
    'media.document': 'Document',
    'media.unknown': 'Unknown',
    'media.dateFrom': 'From Date',
    'media.dateTo': 'To Date',
    'media.filter': 'Filter',
    'media.clear': 'Clear',
    'media.totalMedia': 'Total Media',
    'media.todayMedia': 'Today\'s Media',
    'dashboard.messagesInbound': 'Messages Inbound',
    'dashboard.upcomingAppointments': 'Upcoming Appointments',
    'dashboard.todayAppointments': 'Today\'s Appointments',
    'dashboard.recentActivity': 'Recent Activity',
    'dashboard.loading': 'Loading dashboard data...',

    // TODO Setup
    'setup.title': 'Setup Progress',
    'setup.subtitle': 'Complete these steps to fully configure your clinic',
    'setup.progress': 'Overall Progress',
    'setup.tasksCompleted': 'tasks completed',
    'setup.complete': 'Setup Complete!',
    'setup.completeMessage': 'All configuration tasks have been completed. Your clinic is ready to go!',
    'setup.refreshStatus': 'Refresh Status',
    'setup.goToDashboard': 'Go to Dashboard',
    'setup.highPriority': 'High Priority',
    'setup.mediumPriority': 'Medium Priority',
    'setup.lowPriority': 'Low Priority',
    'setup.priority': 'Priority',

    // 預約
    'appointment.title': 'Appointments',
    'appointment.upcoming': 'Upcoming',
    'appointment.today': 'Today',
    'appointment.past': 'Past',
    'appointment.new': 'New Appointment',
    'appointment.edit': 'Edit Appointment',
    'appointment.delete': 'Delete Appointment',
    'appointment.confirm': 'Confirm Appointment',
    'appointment.cancel': 'Cancel Appointment',
    'appointment.patient': 'Patient',
    'appointment.doctor': 'Doctor',
    'appointment.date': 'Date',
    'appointment.time': 'Time',
    'appointment.status': 'Status',
    'appointment.notes': 'Notes',

    // 病人
    'patient.title': 'Patients',
    'patient.search': 'Search patients...',
    'patient.name': 'Name',
    'patient.phone': 'Phone',
    'patient.email': 'Email',
    'patient.lastVisit': 'Last Visit',
    'patient.appointments': 'Appointments',
    'patient.media': 'Media',
    'patient.details': 'Patient Details',
    'patient.history': 'Medical History',
    'patient.patient': 'Patient',
    'patient.language': 'Language',
    'patient.lastContact': 'Last Contact',
    'patient.viewDetails': 'View Details',
    'patient.languagePreference': 'Language Preference',

    // 醫生
    'doctor.title': 'Doctors',
    'doctor.search': 'Search doctors...',
    'doctor.name': 'Name',
    'doctor.specialization': 'Specialization',
    'doctor.phone': 'Phone',
    'doctor.email': 'Email',
    'doctor.status': 'Status',
    'doctor.active': 'Active',
    'doctor.inactive': 'Inactive',
    'doctor.add': 'Add Doctor',
    'doctor.edit': 'Edit Doctor',
    'doctor.delete': 'Delete Doctor',
    'doctor.save': 'Save Doctor',
    'doctor.cancel': 'Cancel',
    'doctor.male': 'Male',
    'doctor.female': 'Female',
    'doctor.other': 'Other',
    'doctor.doctor': 'Doctor',
    'doctor.clinic': 'Clinic',
    'doctor.facility': 'Facility',
    'doctor.gender': 'Gender',
    'doctor.type': 'Type',
    'doctor.serviceProvided': 'Service Provided',
    'doctor.enterName': 'Enter doctor name',
    'doctor.enterSpecialization': 'Enter specialization',
    'doctor.enterService': 'Enter service provided',
    'doctor.selectGender': 'Select gender',
    'doctor.fillRequired': 'Please fill in all required fields.',
    'doctor.addSuccess': 'Doctor added successfully',
    'doctor.updateSuccess': 'Doctor updated successfully',
    'doctor.deleteSuccess': 'Doctor deleted successfully',
    'doctor.addError': 'Failed to add doctor',
    'doctor.updateError': 'Failed to update doctor',
    'doctor.deleteError': 'Failed to delete doctor',
    'doctor.loadError': 'Failed to load doctors. Please try again later.',
    'doctor.deleteConfirm': 'Are you sure you want to delete this doctor?',
    'doctor.saving': 'Saving...',
    'doctor.noData': 'No doctors found. Add your first doctor using the button above.',
    'doctor.moduleRequirement': 'Module Requirement',
    'doctor.moduleDescription': 'For full functionality, this feature works best with the Structured Data module enabled. When this module is active, doctors\' information can be directly accessed by the AI assistant.',

    // 診所位置
    'location.title': 'Clinic Locations',
    'location.subtitle': 'Manage clinic locations',
    'location.addNew': 'Add New Location',
    'location.edit': 'Edit Location',
    'location.delete': 'Delete Location',
    'location.primaryName': 'Primary Name',
    'location.externalCode': 'External Location Code',
    'location.externalCodePlaceholder': 'Optional external code',
    'location.i18nNames': 'Internationalized Names (Optional)',
    'location.i18nAddresses': 'Internationalized Addresses (Optional)',
    'location.name': 'Name',
    'location.address': 'Address',
    'location.code': 'Code',
    'location.localizedNames': 'Localized Names',
    'location.localizedAddresses': 'Localized Addresses',
    'location.loading': 'Loading locations...',
    'location.loadError': 'Failed to load locations. Please try again later.',
    'location.fillRequired': 'Please fill in the primary name.',
    'location.addSuccess': 'Location added successfully',
    'location.addError': 'Failed to add location. Please try again.',
    'location.updateSuccess': 'Location updated successfully',
    'location.updateError': 'Failed to update location. Please try again.',
    'location.deleteSuccess': 'Location deleted successfully',
    'location.deleteError': 'Failed to delete location. Please try again.',
    'location.deleteConfirm': 'Are you sure you want to delete this location?',
    'location.noLocations': 'No locations found. Add your first location to get started.',

    // 問答知識庫
    'edit': 'Edit',
    'delete': 'Delete',
    'save': 'Save',
    'cancel': 'Cancel',
    'add': 'Add',
    'close': 'Close',
    'confirm': 'Confirm',

    'qa.title': 'Q&A Knowledge Base',
    'qa.search': 'Search questions or answers...',
    'qa.question': 'Question',
    'qa.answer': 'Answer',
    'qa.language': 'Language',
    'qa.status': 'Status',
    'qa.active': 'Active',
    'qa.inactive': 'Inactive',
    'qa.add': 'Add New Q&A',
    'qa.edit': 'Edit Q&A',
    'qa.delete': 'Delete Q&A',
    'qa.save': 'Save Q&A',
    'qa.cancel': 'Cancel',
    'qa.import': 'Import from JSON',
    'qa.export': 'Export to JSON',
    'qa.filter': 'Filter by language',
    'qa.all': 'All Languages',
    'qa.addNewItem': 'Add New QA Item',
    'qa.editItem': 'Edit QA Item',
    'qa.manageTags': 'Manage Tags',
    'qa.importing': 'Importing...',
    'qa.filterByLanguage': 'Filter by language:',
    'qa.filterByTag': 'Filter by tag:',
    'qa.allTags': 'All Tags',
    'qa.searchPlaceholder': 'Search questions or answers...',
    'qa.searching': 'Searching...',
    'qa.noResults': 'No QA knowledge base items found.',
    'qa.noResultsFiltered': 'No QA items found for the selected filters.',
    'qa.tryDifferentFilters': 'Try selecting different filters.',
    'qa.clickToAddFirst': 'Click "Add New QA Item" to create one.',
    'qa.statusInfo': 'Status & Info',
    'qa.actions': 'Actions',
    'qa.rule': 'Rule:',
    'qa.lang': 'Lang:',
    'qa.tags': 'Tags:',
    'qa.deactivate': 'Deactivate',
    'qa.activate': 'Activate',
    'qa.attachRule': 'Attach Rule',
    'qa.attachRuleDescription': 'When the QA item should be included:',
    'qa.attachRuleAlways': 'Always: Include in all conversations',
    'qa.attachRuleIfMatch': 'If Language Match: Include only when conversation language matches the QA language',
    'qa.attachRuleOnHold': 'On Hold: Do not use until manually changed',
    'qa.attachRuleAlwaysLabel': 'Always',
    'qa.attachRuleIfMatchLabel': 'If Language Match',
    'qa.attachRuleOnHoldLabel': 'On Hold',
    'qa.languageDescription': 'Select the language for this QA item.',
    'qa.questionDescription': 'The question that patients might ask.',
    'qa.answerDescription': 'The answer that will be provided to the question.',
    'qa.tagsDescription': 'Select tags to categorize this QA item. Hold Ctrl/Cmd to select multiple tags.',
    'qa.enterQuestion': 'Enter the question',
    'qa.enterAnswer': 'Enter the answer',
    'qa.saving': 'Saving...',
    'qa.update': 'Update',
    'qa.itemDetails': 'QA Item Details',
    'qa.close': 'Close',
    'qa.tagManagement': 'Manage Tags',
    'qa.existingTags': 'Existing Tags',
    'qa.addNewTag': 'Add New Tag',
    'qa.tagName': 'Tag Name',
    'qa.tagColor': 'Color',
    'qa.enterTagName': 'Enter tag name',
    'qa.create': 'Create',
    'qa.noTagsYet': 'No tags created yet. Click "Add New Tag" to create your first tag.',
    'qa.deleteTagConfirm': 'Are you sure you want to delete this tag? This will remove it from all QA items.',
    'qa.deleteItemConfirm': 'Are you sure you want to delete this QA knowledge base item?',
    'qa.loadingItems': 'Loading QA knowledge base...',
    'qa.validJsonRequired': 'Please upload a valid JSON file',
    'qa.jsonArrayRequired': 'The JSON file must contain an array of QA items',
    'qa.noValidItems': 'No valid QA items found in the file',
    'qa.jsonParseError': 'Failed to parse JSON file. Please ensure the file is valid JSON.',
    'qa.fileReadError': 'Failed to read the file',
    'qa.importFailed': 'Failed to import QA items',
    'qa.itemAddedSuccess': 'QA knowledge base item added successfully',
    'qa.itemUpdatedSuccess': 'QA knowledge base item updated successfully',
    'qa.itemDeletedSuccess': 'QA knowledge base item deleted successfully',
    'qa.itemActivatedSuccess': 'QA knowledge base item activated successfully',
    'qa.itemDeactivatedSuccess': 'QA knowledge base item deactivated successfully',
    'qa.itemSaveFailed': 'Failed to save QA knowledge base item',
    'qa.itemDeleteFailed': 'Failed to delete QA knowledge base item',
    'qa.itemStatusUpdateFailed': 'Failed to update QA knowledge base item status',
    'qa.tagCreatedSuccess': 'Tag created successfully',
    'qa.tagUpdatedSuccess': 'Tag updated successfully',
    'qa.tagDeletedSuccess': 'Tag deleted successfully',
    'qa.tagSaveFailed': 'Failed to save tag',
    'qa.tagDeleteFailed': 'Failed to delete tag',
    'qa.tagNameRequired': 'Tag name is required',
    'qa.questionAnswerRequired': 'Question and answer fields are required',
    'qa.chineseTraditional': 'Chinese (Traditional)',
    'qa.chineseSimplified': 'Chinese (Simplified)',
    'qa.englishUS': 'English (US)',
    'qa.importFromJson': 'Import from JSON',
    'qa.addNewQaItem': 'Add New QA Item',
    'qa.allLanguages': 'All Languages',
    'qa.searchQuestionsAnswers': 'Search questions or answers...',
    'qa.editQaItem': 'Edit QA Item',
    'qa.selectLanguageForQaItem': 'Select the language for this QA item.',
    'qa.questionPatientsMightAsk': 'The question that patients might ask.',
    'qa.answerProvidedToQuestion': 'The answer that will be provided to the question.',
    'qa.whenQaItemShouldBeIncluded': 'When the QA item should be included',
    'qa.alwaysInclude': 'Always: Include in all conversations',
    'qa.ifLanguageMatchInclude': 'If Language Match: Include only when conversation language matches the QA language',
    'qa.onHoldDoNotUse': 'On Hold: Do not use until manually changed',
    'qa.selectTagsToCategorizeQaItem': 'Select tags to categorize this QA item',
    'qa.typeToSearchAndSelectTags': 'Type to search and select tags',
    'qa.holdCtrlCmdToSelectMultipleTags': 'Hold Ctrl/Cmd to select multiple tags',
    'qa.noQaKnowledgeBaseItemsFound': 'No QA knowledge base items found. Click "Add New QA Item" to create one.',
    'qa.noQaItemsFoundForSelectedFilters': 'No QA items found for the selected filters',
    'qa.trySelectingDifferentFilters': 'Try selecting different filters.',
    'qa.qaItemDetails': 'QA Item Details',
    'qa.color': 'Color',
    'qa.noTagsCreatedYet': 'No tags created yet. Click "Add New Tag" to create your first tag.',
    'qa.loadingItemsFailed': 'Failed to load QA knowledge base items',

    // 語音信箱
    'voicemail.title': 'Voicemail',
    'voicemail.search': 'Search recordings...',

    // 通話錄音
    'recordings.title': 'Phone Call Recordings',
    'recordings.search.placeholder': 'Search by phone number or transcript...',
    'recordings.filter.all': 'All Recordings',
    'recordings.filter.processed': 'Processed',
    'recordings.filter.pending': 'Pending',
    'recordings.filter.skipped': 'Skipped',
    'recordings.showing': 'Showing {{start}} to {{end}} of {{total}} recordings',
    'recordings.noResults': 'No recordings found matching your criteria',
    'recordings.noRecordings': 'No phone call recordings found',
    'recordings.status.processed': 'Processed',
    'recordings.status.pending': 'Pending',
    'recordings.status.skipped': 'Skipped',
    'recordings.table.caller': 'Caller',
    'recordings.table.recipient': 'Recipient',
    'recordings.table.transcript': 'Transcript',
    'recordings.table.date': 'Date',
    'recordings.table.duration': 'Duration',
    'recordings.table.status': 'Status',
    'recordings.table.actions': 'Actions',
    'recordings.actions.view': 'View',
    'recordings.actions.listen': 'Listen',
    'recordings.noTranscript': 'No transcript available',
    'recordings.noDuration': 'Unknown',
    'recordings.detail.title': 'Phone Call Recording',
    'recordings.detail.backToRecordings': 'Back to Recordings',
    'recordings.detail.notFound': 'Recording not found',
    'recordings.detail.caller': 'Caller',
    'recordings.detail.recipient': 'Recipient',
    'recordings.detail.date': 'Date',
    'recordings.detail.time': 'Time',
    'recordings.detail.duration': 'Duration',
    'recordings.detail.conversationId': 'Conversation ID',
    'recordings.detail.source': 'Source',
    'recordings.detail.audioRecording': 'Audio Recording',
    'recordings.detail.audioNotSupported': 'Your browser does not support the audio element.',
    'recordings.detail.downloadAudio': 'Download Audio File',

    // Voice Mail
    'voiceMail.title': 'Voice Mail Transcription',
    'voiceMail.transcription': 'Transcription',
    'voiceMail.audioRecording': 'Audio Recording',
    'voiceMail.audioNotSupported': 'Your browser does not support the audio element.',
    'voiceMail.downloadAudio': 'Download Audio File',
    'voiceMail.duration': 'Duration',
    'voiceMail.noDuration': 'Unknown',
    'voiceMail.minutes': 'min',
    'voiceMail.seconds': 'sec',
    'recordings.detail.transcript': 'Transcript',
    'recordings.detail.editTranscript': 'Edit',
    'recordings.detail.expand': 'Expand',
    'recordings.detail.collapse': 'Collapse',
    'recordings.detail.save': 'Save',
    'recordings.detail.saving': 'Saving...',
    'recordings.detail.cancel': 'Cancel',
    'recordings.detail.transcriptPlaceholder': 'Enter transcript here...',
    'recordings.detail.noTranscript': 'No transcript available',
    'recordings.detail.noDuration': 'Unknown',
    'voicemail.date': 'Date',
    'voicemail.duration': 'Duration',
    'voicemail.caller': 'Caller',
    'voicemail.status': 'Status',
    'voicemail.play': 'Play',
    'voicemail.download': 'Download',
    'voicemail.delete': 'Delete',

    // 數據導入
    'import.title': 'Data Import',
    'import.selectFile': 'Select File',
    'import.upload': 'Upload',
    'import.processing': 'Processing...',
    'import.success': 'Import successful',
    'import.error': 'Import failed',
    'import.format': 'Supported formats: Excel (.xlsx)',
    'import.appointmentReminder': 'Appointment Reminder',
    'import.appointmentReminderDescription': 'Send appointment reminders to patients via WhatsApp',
    'import.invalidFileType': 'Please select a valid Excel (.xlsx) file',
    'import.downloadFailed': 'Failed to download template. Please try again or contact support.',
    'import.selectFileFirst': 'Please select a file to upload',
    'import.uploadError': 'Error uploading file',
    'import.processingError': 'Error processing import',
    'import.step1': 'Upload File',
    'import.step2': 'Review Data',
    'import.step3': 'Process Data',
    'import.importType': 'Import Type',
    'import.template': 'Template',
    'import.downloadTemplate': 'Download Template Example',
    'import.templateDescription': 'Download the template file to see the required format for',
    'import.uploadExcelFile': 'Upload Excel File',
    'import.uploading': 'Uploading...',
    'import.uploadAndPreview': 'Upload and Preview',
    'import.importedDataPreview': 'Imported Data Preview',
    'import.filename': 'Filename',
    'import.records': 'Records',
    'import.processData': 'Process Data',
    'import.processingComplete': 'Import Processing Complete',
    'import.startNewImport': 'Start New Import',

    // 設置
    'settings.title': 'Account Settings',
    'settings.subtitle': 'Manage your account preferences and security settings',
    'settings.currentInfo': 'Current Account Information',
    'settings.username': 'Username',
    'settings.email': 'Email',
    'settings.clinicId': 'Clinic ID',
    'settings.updateUsername': 'Update Username',
    'settings.newUsername': 'New Username',
    'settings.enterNewUsername': 'Enter new username',
    'settings.currentPassword': 'Current Password',
    'settings.enterCurrentPassword': 'Enter current password',
    'settings.updatePassword': 'Update Password',
    'settings.newPassword': 'New Password',
    'settings.enterNewPassword': 'Enter new password',
    'settings.confirmPassword': 'Confirm New Password',
    'settings.confirmNewPassword': 'Confirm new password',

    // 設置錯誤訊息
    'error.allFieldsRequired': 'All fields are required',
    'error.usernameUnchanged': 'New username must be different from current username',
    'error.passwordMismatch': 'New passwords do not match',
    'error.passwordUnchanged': 'New password must be different from current password',
    'error.updateFailed': 'Update failed',

    // 設置2FA訊息
    'settings.2faNote': 'For security, we\'ll send a verification code to your phone number',
    'settings.tempUserPasswordNote': 'For security, we\'ll send a verification code to your phone number to confirm the password change.',
    'settings.completeProfile': 'Complete Your Profile',
    'settings.tempUserMessage': 'Please set up your account with a username, email, and password to continue.',
    'settings.phoneNumber': 'Phone Number',
    'settings.password': 'Password',
    'settings.enterUsername': 'Enter your username',
    'settings.enterEmail': 'Enter your email address',
    'settings.enterPassword': 'Enter your password',
    'settings.completeSetup': 'Complete Account Setup',
    'error.invalidEmail': 'Please enter a valid email address',
    'error.conversionFailed': 'Account conversion failed',
    'common.processing': 'Processing...',
    'settings.verifyUpdate': 'Verify and Update',

    // 通用
    'common.language': 'Language',
    'common.search': 'Search',
    'common.filter': 'Filter',
    'common.sort': 'Sort',
    'common.loading': 'Loading...',
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.add': 'Add',
    'common.close': 'Close',
    'common.confirm': 'Confirm',
    'common.yes': 'Yes',
    'common.no': 'No',
    'common.ok': 'OK',
    'common.error': 'Error',
    'common.success': 'Success',
    'common.warning': 'Warning',
    'common.info': 'Information',
    'common.next': 'Next',
    'common.previous': 'Previous',
    'common.first': 'First',
    'common.last': 'Last',
    'common.page': 'Page',
    'common.of': 'of',
    'common.items': 'items',
    'common.showing': 'Showing',
    'common.to': 'to',
    'common.noData': 'No data available',
    'common.selectAll': 'Select All',
    'common.deselectAll': 'Deselect All',
    'common.actions': 'Actions',
    'common.back': 'Back',
    'common.updating': 'Updating...',
    'common.retry': 'Retry',

    // Conversation Activities
    'conversationActivities.title': 'Conversation Activities',
    'conversationActivities.subtitle': 'View all conversation activities and events',
    'conversationActivities.search': 'Search by conversation ID or message content...',
    'conversationActivities.noResults': 'No activities found',
    'conversationActivities.loading': 'Loading activities...',
    'conversationActivities.error': 'Error loading activities',
    'conversationActivities.conversationId': 'Conversation ID',
    'conversationActivities.eventType': 'Event Type',
    'conversationActivities.timestamp': 'Timestamp',
    'conversationActivities.customer': 'Customer',
    'conversationActivities.channel': 'Channel',
    'conversationActivities.seen': 'Seen',
    'conversationActivities.unseen': 'Unseen',
    'conversationActivities.showUnseenOnly': 'Show Unseen Only',
    'conversationActivities.groupByConversation': 'Group by Conversation',
    'conversationActivities.dateFrom': 'Start Date',
    'conversationActivities.dateTo': 'End Date',

    // Users Management
    'users.title': 'User Management',
    'users.create': 'Create User',
    'users.edit': 'Edit User',
    'users.update': 'Update User',
    'users.accessDenied': 'Access Denied',
    'users.adminOnly': 'Only admin users can access this page',
    'users.role.admin': 'Admin',
    'users.role.manager': 'Manager',
    'users.role.user': 'User',
    'users.role.viewer': 'Viewer',
    'users.status.active': 'Active',
    'users.status.inactive': 'Inactive',
    'users.never': 'Never',
    'users.actions.edit': 'Edit',
    'users.actions.deactivate': 'Deactivate',
    'users.actions.generatePassword': 'Generate',
    'users.actions.copyPassword': 'Copy Password',
    'users.actions.showPassword': 'Show Password',
    'users.actions.hidePassword': 'Hide Password',
    'users.form.username': 'Username',
    'users.form.email': 'Email',
    'users.form.password': 'Password',
    'users.form.passwordPlaceholder': 'Enter password or generate one',
    'users.form.passwordGenerated': 'Password generated',
    'users.form.characters': 'characters',
    'users.form.role': 'Role',
    'users.form.phoneNumber': 'Phone Number',
    'users.table.user': 'User',
    'users.table.email': 'Email',
    'users.table.role': 'Role',
    'users.table.status': 'Status',
    'users.table.lastLogin': 'Last Login',
    'users.table.actions': 'Actions',
    'users.success.created': 'User created successfully',
    'users.success.updated': 'User updated successfully',
    'users.success.deactivated': 'User deactivated successfully',
    'users.success.passwordGenerated': 'Password generated successfully',
    'users.success.passwordCopied': 'Password copied to clipboard',
    'users.error.fetchFailed': 'Failed to fetch users',
    'users.error.createFailed': 'Failed to create user',
    'users.error.updateFailed': 'Failed to update user',
    'users.error.deactivateFailed': 'Failed to deactivate user',
    'users.error.copyFailed': 'Failed to copy password to clipboard',
    'users.confirm.deactivate': 'Are you sure you want to deactivate this user?',
    'conversationActivities.filter': 'Filter',
    'conversationActivities.clear': 'Clear',
    'conversationActivities.totalActivities': 'Total Activities',
    'conversationActivities.todayActivities': 'Today\'s Activities',
    'conversationActivities.itemsPerPage': 'Items per page',
    'conversationActivities.last7Days': 'Last 7 days',
    'conversationActivities.last30Days': 'Last 30 days',
    'conversationActivities.conversationIdFilter': 'Conversation ID Filter',
    'conversationActivities.filteredByConversationId': 'Filtered by conversation ID...',
    'conversationActivities.clearFilter': 'Clear Filter',
    'conversationActivities.selected': 'selected',
    'conversationActivities.setSeenForSelected': 'Set Seen for Selected',
    'conversationActivities.dateFilter': 'Date Filter',
    'conversationActivities.quickFilters': 'Quick Filters',
    'conversationActivities.clearAllFilters': 'Clear All Filters',

    // Appointment Event Values
    'appointment.doctor': 'Doctor',
    'appointment.date': 'Date',
    'appointment.time': 'Time',
    'appointment.remarks': 'Remarks',
    'appointment.notAvailable': 'N/A',

    // Appointment Info Event Values
    'appointmentInfo.submitForm': 'Submit Form',
    'appointmentInfo.updateInfo': 'Appointment Info Update',
    'appointmentInfo.appointmentInfo': 'Appointment Info',
    'appointmentInfo.patientData': 'Patient Data',
    'appointmentInfo.name': 'Name',
    'appointmentInfo.dateOfBirth': 'Date of Birth',
    'appointmentInfo.gender': 'Gender',
    'appointmentInfo.phone': 'Phone',
    'appointmentInfo.email': 'Email',
    'appointmentInfo.language': 'Language',
    'appointmentInfo.patientType': 'Patient Type',
    'appointmentInfo.preferences': 'Appointment Preferences',
    'appointmentInfo.preferredLocation': 'Preferred Location',
    'appointmentInfo.preferredDoctor': 'Preferred Doctor',
    'appointmentInfo.preferredDoctor2': 'Preferred Doctor (2)',
    'appointmentInfo.preferredDoctor3': 'Preferred Doctor (3)',
    'appointmentInfo.preferredDate': 'Preferred Date',
    'appointmentInfo.preferredTime': 'Preferred Time',
    'appointmentInfo.additionalInfo': 'Additional Information',
    'appointmentInfo.symptoms': 'Symptoms',
    'appointmentInfo.specialRequest': 'Special Request',
    'appointmentInfo.bookingType': 'Booking Type',
    'appointmentInfo.systemInfo': 'System Information',
    'appointmentInfo.appointmentInfoId': 'Appointment Info ID',

    // Appointment Offer Event Values
    'appointmentOffer.title': 'Appointment Offer',
    'appointmentOffer.doctor': 'Doctor',
    'appointmentOffer.specialty': 'Specialty',
    'appointmentOffer.date': 'Date',
    'appointmentOffer.time': 'Time',
    'appointmentOffer.location': 'Location',
    'appointmentOffer.systemInfo': 'System Information',
    'appointmentOffer.appointmentInfoId': 'Appointment Info ID',
    'appointmentOffer.appointmentOfferId': 'Appointment Offer ID',

    // Failed Appointment Event Values
    'failedAppointment.title': 'Appointment Failed',
    'failedAppointment.customerName': 'Customer Name',
    'failedAppointment.notAvailable': 'N/A',
    'failedAppointment.error': 'Error',
    'failedAppointment.unknownError': 'Unknown Error',
    'failedAppointment.rawErrorInfo': 'Raw Error Information',
    'failedAppointment.technicalDetails': 'Technical Details',
    'failedAppointment.errorCode': 'Error Code',
    'failedAppointment.errorCategory': 'Error Category',
    'failedAppointment.systemInfo': 'System Information',
    'failedAppointment.confirmedEventId': 'Appointment Confirmation ID',
    'failedAppointment.appointmentInfoId': 'Appointment Info ID',
    'failedAppointment.appointmentOfferId': 'Appointment Offer ID',

    // Create Appointment Event Values
    'createAppointment.title': 'Create Appointment',
    'createAppointment.doctor': 'Doctor',
    'createAppointment.date': 'Date',
    'createAppointment.time': 'Time',
    'createAppointment.location': 'Location',
    'createAppointment.remarks': 'Remarks',
    'createAppointment.notAvailable': 'N/A',
    'createAppointment.systemInfo': 'System Information',
    'createAppointment.confirmedEventId': 'Appointment Confirmation ID',
    'createAppointment.appointmentInfoId': 'Appointment Info ID',
    'createAppointment.appointmentOfferId': 'Appointment Offer ID',

    // Confirm Appointment Offer Event Values
    'confirmAppointment.title': 'Confirm Appointment Time',
    'confirmAppointment.status': 'Status',
    'confirmAppointment.confirmed': 'Confirmed',
    'confirmAppointment.cancelled': 'Cancelled',
    'confirmAppointment.pending': 'Pending',
    'confirmAppointment.unknownStatus': 'Unknown Status',
    'confirmAppointment.doctor': 'Doctor',
    'confirmAppointment.date': 'Date',
    'confirmAppointment.time': 'Time',
    'confirmAppointment.location': 'Location',
    'confirmAppointment.systemInfo': 'System Information',
    'confirmAppointment.confirmedEventId': 'Appointment Confirmation ID',
    'confirmAppointment.appointmentInfoId': 'Appointment Info ID',
    'confirmAppointment.appointmentOfferId': 'Appointment Offer ID',

    // Event Types
    'eventType.clinicNotification': 'Clinic Notification',
    'eventType.leaveMessage': 'Leave Message',
    'eventType.createAppointment': 'Create Appointment',
    'eventType.noMessageContent': 'No message content',
    'eventType.jsonData': 'JSON Data',
    'eventType.viewJson': 'View JSON',
    'eventType.createAppointmentOffer': 'Create Appointment Offer',
    'eventType.confirmAppointmentOffer': 'Confirm Appointment Offer',
    'eventType.submitForm': 'Submit Form',
    'eventType.updateAppointmentInfo': 'Update Appointment Info',
    'eventType.failedAppointment': 'Failed Appointment',
    'eventType.transcribeVoiceMail': 'Transcribe Voice Mail',
    'eventType.mediaUpload': 'Media Upload',
    'eventType.escalateMessage': 'Team Escalation',

    // Escalate Message
    'escalateMessage.title': 'Team Escalation',
    'escalateMessage.requestId': 'Request ID',
    'escalateMessage.receivedAt': 'Received at',
    'escalateMessage.teamsNotified': 'Teams notified',
    'escalateMessage.customerWhatsApp': 'Customer WhatsApp',
    'escalateMessage.openConversation': 'Open WhatsApp conversation',
    'escalateMessage.recipients': 'Recipients',
    'escalateMessage.notAvailable': 'N/A',
    'escalateMessage.severity.low': 'Low',
    'escalateMessage.severity.medium': 'Medium',
    'escalateMessage.severity.high': 'High',
    'escalateMessage.severity.critical': 'Critical',
    'escalateMessage.team.subscription_list': 'Subscription list',

    // Conversation Activities Page
    'conversationActivities.loadingActivities': 'Loading activities...',
    'conversationActivities.noActivitiesFound': 'No conversation activities found.',
    'conversationActivities.conversationActivities': 'Conversation Activities',
    'conversationActivities.total': 'total',
    'conversationActivities.conversationGroup': 'Conversation Group',
    'conversationActivities.events': 'events',
    'conversationActivities.expandAll': 'Expand All',
    'conversationActivities.collapseAll': 'Collapse All',
    'conversationActivities.expandGroup': 'Expand Group',
    'conversationActivities.collapseGroup': 'Collapse Group',
    'conversationActivities.displayed': 'displayed',
    'conversationActivities.selectGroup': 'Select Group',
    'conversationActivities.deselectGroup': 'Deselect Group',
    'conversationActivities.viewPatientDetails': 'View Patient Details',
    'conversationActivities.timestamp': 'Timestamp',
    'conversationActivities.eventType': 'Event Type',
    'conversationActivities.phoneNumber': 'Phone Number',
    'conversationActivities.message': 'Message',
    'conversationActivities.actions': 'Actions',
    'conversationActivities.clickToFilterByConversation': 'Click to filter by this conversation',
    'conversationActivities.seen': 'Seen',
    'conversationActivities.setSeen': 'Set Seen',
    'conversationActivities.setting': 'Setting...',
    'conversationActivities.internalRemark': 'Internal Remark',
    'conversationActivities.addInternalRemark': 'Add Remark',
    'conversationActivities.editInternalRemark': 'Edit Remark',
    'conversationActivities.internalRemarkPlaceholder': 'Enter internal remark...',
    'conversationActivities.save': 'Save',
    'conversationActivities.cancel': 'Cancel',
    'conversationActivities.saving': 'Saving...',
    'conversationActivities.showingPage': 'Showing page',
    'conversationActivities.of': 'of',
    'conversationActivities.totalItems': 'total items',
    'conversationActivities.previous': 'Previous',
    'conversationActivities.next': 'Next',
    'conversationActivities.whatsapp': 'WhatsApp',
    'conversationActivities.openWhatsApp': 'Open WhatsApp chat',
    'conversationActivities.noPhoneNumber': 'No phone number available',

    // Calendar specific translations
    'calendar.system': 'System',
    'calendar.loadAppointmentsFailed': 'Failed to load appointments, please try again later',
    'calendar.pendingConfirmation': 'Pending Confirmation',
    'calendar.clinicRequest': 'Clinic Request',
    'calendar.minutes': 'minutes',
    'calendar.doctorUnavailable': 'Doctor is not available at this time',
    'calendar.appointmentUpdatedConfirmed': 'Appointment updated and confirmed',
    'calendar.saveAppointmentFailed': 'Failed to save appointment, please try again later',
    'calendar.appointmentConfirmed': 'Appointment confirmed',
    'calendar.confirmAppointmentFailed': 'Failed to confirm appointment, please try again later',
    'calendar.appointmentCancelled': 'Appointment cancelled',
    'calendar.cancelAppointmentFailed': 'Failed to cancel appointment, please try again later',
    'calendar.pleaseSelectDoctor': 'Please select a doctor',
    'calendar.pleaseSelectDateRange': 'Please select a date range',
    'calendar.scheduleUpdated': 'Schedule updated',
    'calendar.scheduleCreated': 'Schedule created',
    'calendar.saveScheduleFailed': 'Failed to save schedule, please try again later',
    'calendar.loadScheduleFailed': 'Failed to load schedule data, please try again later',
    'calendar.confirmDeleteSchedule': 'Are you sure you want to delete this schedule?',
    'calendar.scheduleDeleted': 'Schedule deleted',
    'calendar.deleteScheduleFailed': 'Failed to delete schedule, please try again later',
    'calendar.blockScheduleUpdated': 'Holiday updated successfully',
    'calendar.blockScheduleCreated': 'Holiday created successfully',
    'calendar.blockScheduleDeleted': 'Holiday deleted successfully',
    'calendar.saveBlockScheduleFailed': 'Failed to save holiday',
    'calendar.loadBlockScheduleFailed': 'Failed to load holidays',
    'calendar.deleteBlockScheduleFailed': 'Failed to delete holiday',
    'calendar.confirmDeleteBlockSchedule': 'Are you sure you want to delete this holiday?',
    'calendar.pleaseEnterReason': 'Please enter a reason',
    'calendar.pleaseSelectDateTimeRange': 'Please select date and time range',
    'calendar.timeSlotBlocked': 'This time slot is blocked',
    'calendar.blockedSlots': 'Blocked time slots',
    'calendar.today': 'Today',
    'calendar.days': ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    'calendar.search': 'Search',
    'calendar.searchAppointments': 'Search appointments...',
    'calendar.doctors': 'Doctors',
    'calendar.viewMore': 'View more...',
    'calendar.hideCalendar': 'Hide Calendar',
    'calendar.showCalendar': 'Show Calendar',
    'calendar.manageSchedule': 'Manage Schedule',
    'calendar.manageBlockSchedule': 'Manage Holiday',
    'calendar.appointments': 'appointments',
    'calendar.noAppointments': 'No appointments',
    'calendar.addAppointment': 'Add Appointment',
    'calendar.appointmentRequestSummary': 'Appointment Request Summary',
    'calendar.confirmed': 'Confirmed',
    'calendar.cancelled': 'Cancelled',
    'calendar.unknown': 'Unknown',
    'calendar.moreAppointments': 'more appointments...',
    'calendar.outOfHoursWarning': 'Non-office-hours appointments.',
    'calendar.availableSlots': 'Available time slots',
    'calendar.unavailableSlots': 'Unavailable time slots',
    'calendar.doctorScheduleManagement': 'Doctor Schedule Management',
    'calendar.blockScheduleManagement': 'Holiday Management',
    'calendar.selectDoctor': 'Select Doctor',
    'calendar.pleaseSelectDoctor': 'Please select a doctor',
    'calendar.addSchedule': 'Add Schedule',
    'calendar.addBlockSchedule': 'Add Holiday',
    'calendar.close': 'Close',
    'calendar.editAppointment': 'Edit Appointment',
    'calendar.newAppointment': 'New Appointment',
    'calendar.cancel': 'Cancel',
    'calendar.title': 'Title',
    'calendar.customerName': 'Customer Name',
    'calendar.enterCustomerName': 'Enter customer name',
    'calendar.date': 'Date',
    'calendar.time': 'Time',
    'calendar.doctor': 'Doctor',
    'calendar.selectDoctor': 'Select Doctor',
    'calendar.duration': 'Duration (minutes)',
    'calendar.remarks': 'Remarks',
    'calendar.enterRemarks': 'Enter remarks (optional)',
    'calendar.processing': 'Processing...',
    'calendar.updateAppointment': 'Update Appointment',
    'calendar.submitAppointment': 'Submit Appointment',
    'calendar.appointmentActions': 'Appointment Actions',
    'calendar.contactPhone': 'Contact Phone:',
    'calendar.appointmentDate': 'Date:',
    'calendar.appointmentTime': 'Time:',
    'calendar.appointmentDoctor': 'Doctor:',
    'calendar.appointmentStatus': 'Status:',
    'calendar.appointmentRemarks': 'Remarks:',
    'calendar.cancelAppointment': 'Cancel Appointment',
    'calendar.confirmAppointment': 'Confirm Appointment',
    'calendar.editAndConfirm': 'Edit and Confirm',
    'calendar.editAppointment': 'Edit Appointment',

    // Schedule Dialog
    'scheduleDialog.editSchedule': 'Edit Schedule',
    'scheduleDialog.addSchedule': 'Add Schedule',
    'scheduleDialog.selectDoctor': 'Select Doctor',
    'scheduleDialog.pleaseSelectDoctor': 'Please select doctor',
    'scheduleDialog.defaultSchedule': 'Default Schedule',
    'scheduleDialog.setAsDefault': 'Set as default schedule',
    'scheduleDialog.startDate': 'Start Date',
    'scheduleDialog.endDate': 'End Date',
    'scheduleDialog.defaultScheduleNoDateRange': 'Default schedule does not require date range',
    'scheduleDialog.weeklySchedule': 'Weekly Schedule',
    'scheduleDialog.monday': 'Monday',
    'scheduleDialog.tuesday': 'Tuesday',
    'scheduleDialog.wednesday': 'Wednesday',
    'scheduleDialog.thursday': 'Thursday',
    'scheduleDialog.friday': 'Friday',
    'scheduleDialog.saturday': 'Saturday',
    'scheduleDialog.sunday': 'Sunday',
    'scheduleDialog.applyToAllDays': 'Apply to all days',
    'scheduleDialog.sortTimeSlots': 'Sort time slots',
    'scheduleDialog.addTimeSlot': 'Add time slot',
    'scheduleDialog.startTime': 'Start Time',
    'scheduleDialog.endTime': 'End Time',
    'scheduleDialog.removeTimeSlot': 'Remove time slot',
    'scheduleDialog.noScheduleForDay': 'No schedule for this day',
    'scheduleDialog.updateSchedule': 'Update Schedule',
    'scheduleDialog.createSchedule': 'Create Schedule',
    'scheduleDialog.confirmApplyToAllDays': 'Are you sure you want to apply this day\'s schedule to all days?',

    // Schedule List
    'scheduleList.noSchedules': 'No schedules created yet. Please click "Add Schedule" button to create schedules.',
    'scheduleList.establishedSchedules': 'Established Schedules',
    'scheduleList.scheduleId': 'Schedule ID',
    'scheduleList.default': 'Default',
    'scheduleList.defaultScheduleNoDateRange': 'Default schedule (no date restrictions)',
    'scheduleList.validPeriod': 'Valid period',
    'scheduleList.to': 'to',
    'scheduleList.noSchedule': 'No schedule',

    // Doctor schedules JSON bulk export/import
    'doctorSchedulesJson.title': 'JSON export / configure all',
    'doctorSchedulesJson.hide': 'Hide',
    'doctorSchedulesJson.show': 'Show',
    'doctorSchedulesJson.export': 'Export JSON',
    'doctorSchedulesJson.upload': 'Upload JSON',
    'doctorSchedulesJson.replaceListed': 'replace listed',
    'doctorSchedulesJson.upsert': 'upsert',
    'doctorSchedulesJson.import': 'Import / configure',
    'doctorSchedulesJson.exportOk': 'Exported doctor schedules JSON.',
    'doctorSchedulesJson.exportFail': 'Failed to export',
    'doctorSchedulesJson.invalidJson': 'Invalid JSON file.',
    'doctorSchedulesJson.needArray': 'JSON must include a non-empty doctor_schedules array.',
    'doctorSchedulesJson.confirmImport': 'Import doctor schedules from JSON?',
    'doctorSchedulesJson.importOk': 'Import completed.',
    'doctorSchedulesJson.importFail': 'Failed to import',

    // Block Schedule Dialog
    'blockScheduleDialog.editBlockSchedule': 'Edit Holiday',
    'blockScheduleDialog.addBlockSchedule': 'Add Holiday',
    'blockScheduleDialog.selectDoctor': 'Select Doctor',
    'blockScheduleDialog.allDoctors': 'All Doctors',
    'blockScheduleDialog.reason': 'Reason',
    'blockScheduleDialog.reasonPlaceholder': 'Enter reason (e.g., Public holiday, Leave)',
    'blockScheduleDialog.blockFrom': 'From',
    'blockScheduleDialog.blockUntil': 'Until',
    'blockScheduleDialog.quickSelect': 'Quick Select',
    'blockScheduleDialog.today': 'Today',
    'blockScheduleDialog.tomorrow': 'Tomorrow',
    'blockScheduleDialog.thisWeek': 'This Week',
    'blockScheduleDialog.nextWeek': 'Next Week',
    'blockScheduleDialog.updateBlockSchedule': 'Update Holiday',
    'blockScheduleDialog.createBlockSchedule': 'Create Holiday',

    // Block Schedule List (Holiday)
    'blockScheduleList.noBlockSchedules': 'No holidays yet. Click "Add Holiday" to create one.',
    'blockScheduleList.establishedBlockSchedules': 'Holidays',
    'blockScheduleList.blockScheduleId': 'ID',
    'blockScheduleList.inactive': 'Inactive',
    'blockScheduleList.active': 'Active',
    'blockScheduleList.upcoming': 'Upcoming',
    'blockScheduleList.expired': 'Expired',
    'blockScheduleList.reason': 'Reason',
    'blockScheduleList.duration': 'Duration',
    'blockScheduleList.blockFrom': 'From',
    'blockScheduleList.blockUntil': 'Until',
    'blockScheduleList.affectedDoctor': 'Affected Doctor',
    'blockScheduleList.scope': 'Scope',
    'blockScheduleList.allDoctors': 'All Doctors',
    'blockScheduleList.createdAt': 'Created',
    'blockScheduleList.updatedAt': 'Updated',

    // Meta WhatsApp Templates
    'metaTemplates.title': 'Meta WhatsApp Templates',
    'metaTemplates.description': 'Create and manage WhatsApp message templates for marketing and utility messages',
    'metaTemplates.createTemplate': 'Create Template',
    'metaTemplates.allStatuses': 'All Statuses',
    'metaTemplates.approved': 'Approved',
    'metaTemplates.pending': 'Pending',
    'metaTemplates.rejected': 'Rejected',
    'metaTemplates.name': 'Template Name',
    'metaTemplates.category': 'Category',
    'metaTemplates.language': 'Language',
    'metaTemplates.status': 'Status',
    'metaTemplates.variables': 'Variables',
    'metaTemplates.header': 'Header',
    'metaTemplates.body': 'Body',
    'metaTemplates.footer': 'Footer',
    'metaTemplates.buttons': 'Buttons',
    'metaTemplates.templateName': 'Template Name',
    'metaTemplates.descriptionField': 'Description',
    'metaTemplates.downloadCSV': 'Download CSV Template',
    'metaTemplates.sendBroadcast': 'Send Broadcast',
    'metaTemplates.downloadCSVTemplate': 'Download CSV Template',
    'metaTemplates.uploadCSV': 'Upload CSV File',
    'metaTemplates.selectCSVFile': 'Click to select CSV file',
    'metaTemplates.variableExamples': 'Variable Examples',
    'metaTemplates.variableExamplesNote': 'Provide example values for each variable. These examples will be shown to Meta reviewers.',
    'metaTemplates.typeOfVariable': 'Type of variable',
    'metaTemplates.variableTypeName': 'Name',
    'metaTemplates.variableTypeNumber': 'Number',
    'metaTemplates.templateDetails': 'Template Details',
    'metaTemplates.templateId': 'Template ID',
    'metaTemplates.templateComponents': 'Template Components',
    'metaTemplates.rawTemplateData': 'Raw Template Data',
    'metaTemplates.noTemplateData': 'No template data',
    'metaTemplates.templateInfoNotLoaded': 'Template information could not be loaded.',
    'metaTemplates.parameterFormat': 'Parameter Format',
    'metaTemplates.positionalFormat': 'Positional ({{1}}, {{2}}, ...)',
    'metaTemplates.namedFormat': 'Named ({{variable_name}})',
    'metaTemplates.positionalFormatHelp': 'Variables will be converted to {{1}}, {{2}}, etc. in order.',
    'metaTemplates.namedFormatHelp': 'Variables will keep their names: {{variable_name}}',
    'metaTemplates.variableHintPositional': 'Use {{variable_name}} to add variables. They will be converted to {{1}}, {{2}}, etc.',
    'metaTemplates.variableHintNamed': 'Use {{variable_name}} to add variables. Variable names must be lowercase with underscores.',
    'metaTemplates.resend': 'Resend Template',
    'metaTemplates.confirmResend': 'Are you sure you want to resend this template to Meta for review?',
    'metaTemplates.resendSuccess': 'Template resent successfully!',
    'metaTemplates.resendError': 'Failed to resend template',
    'common.refresh': 'Refresh',
    'common.loading': 'Loading...',
    'common.close': 'Close',
    'common.submitting': 'Submitting...',
    'common.submit': 'Submit for Review',
    'common.sending': 'Sending...',

    // Embedded Signup
    'embeddedSignup.title': 'Meta Embedded Signup',
    'embeddedSignup.description': 'Use this page to connect your existing WhatsApp Business Account via Meta Embedded Signup.',
    'embeddedSignup.button.label': 'Start Embedded Signup Request',
    'embeddedSignup.button.loading': 'Preparing...',
    'embeddedSignup.button.caption': "This will launch Meta's Embedded Signup flow in a new window.",
    'embeddedSignup.placeholderSuccess': 'Embedded Signup request started (placeholder).',
    'embeddedSignup.placeholderError': 'Unable to start Embedded Signup yet. Please try again later.',
    'embeddedSignup.status.sdkTitle': 'Facebook SDK',
    'embeddedSignup.status.sdkReady': 'Facebook SDK is ready.',
    'embeddedSignup.status.sdkLoading': 'Waiting for Facebook SDK to finish loading...',
    'embeddedSignup.status.codeTitle': 'Authorization Code',
    'embeddedSignup.status.codeIdle': 'No authorization code detected yet.',
    'embeddedSignup.status.codeProcessing': 'Processing the authorization code...',
    'embeddedSignup.status.codeSuccess': 'Authorization code exchanged successfully.',
    'embeddedSignup.status.codeError': 'Failed to exchange authorization code.',
    'embeddedSignup.status.latestResult': 'Latest Result',
    'embeddedSignup.status.wabaLabel': 'WABA ID:',
    'embeddedSignup.status.expiresLabel': 'Token expires at:',
    'embeddedSignup.status.noResult': 'No onboarding result yet.',
    'embeddedSignup.status.sessionTitle': 'Session Events',
    'embeddedSignup.status.sessionEvent': 'Event: {{event}}',
    'embeddedSignup.status.sessionTimestamp': 'Received at: {{value}}',
    'embeddedSignup.status.sessionNone': 'No session events captured yet.',
    'embeddedSignup.success.exchange': 'WhatsApp onboarding completed.',
    'embeddedSignup.error.exchange': 'Unable to complete WhatsApp onboarding.',
    'embeddedSignup.error.sdkNotReady': 'Facebook SDK is still loading. Please wait and try again.',
    'embeddedSignup.error.httpsRequired': 'Facebook Login requires HTTPS (or localhost). Please use an HTTPS domain such as your ngrok URL.',
    'embeddedSignup.error.missingConfig': 'Embedded Signup configuration is missing. Please set the Meta App ID and Config ID.',
    'embeddedSignup.error.fbCancelled': 'Meta login was cancelled.',
    'embeddedSignup.accessRestricted': 'Access Restricted',
    'embeddedSignup.accessRestrictedMessage': 'This feature is only available for authorized clinics.',
    'embeddedSignup.instruction.step1Title': '1. Send a message first:',
    'embeddedSignup.instruction.step1Text': 'From your phone (the number in Subscriptions page), send any text message (e.g. "Hi") to the business WhatsApp number you want to connect.',
    'embeddedSignup.instruction.step2Title': '2. Start the signup process:',
    'embeddedSignup.instruction.step2Text': 'Click the "Start Embedded Signup Request" button below and follow the on-screen instructions to complete the Meta authorization.',
    'embeddedSignup.instruction.step3Title': '3. Receive confirmation:',
    'embeddedSignup.instruction.step3Text': 'Once the setup is complete, you will receive a confirmation message on your phone.',

    // Token onboarding
    'onboarding.title': 'Clinic Onboarding',
    'onboarding.loading': 'Loading onboarding flow...',
    'onboarding.invalidLink.title': 'Invalid onboarding link',
    'onboarding.stepCounter': 'Step {{current}} of {{total}}',
    'onboarding.clinicName': 'Clinic name:',
    'onboarding.region': 'Region:',
    'onboarding.continue': 'Continue',
    'onboarding.saving': 'Saving...',
    'onboarding.creating': 'Creating...',
    'onboarding.submitting': 'Submitting...',
    'onboarding.confirmClinic.title': 'Confirm your clinic details',
    'onboarding.confirmClinic.button': 'Confirm and Continue',
    'onboarding.email.title': 'Provide admin email',
    'onboarding.password.title': 'Set admin password',
    'onboarding.password.button': 'Create Admin and Continue',
    'onboarding.embedded.title': 'Connect WhatsApp (Embedded Signup)',
    'onboarding.embedded.description': 'Use Meta Embedded Signup to connect your WhatsApp number to Nexi.',
    'onboarding.embedded.start': 'Start Embedded Signup',
    'onboarding.embedded.connected': 'Connected',
    'onboarding.questionnaire.title': 'Tell us about your business',
    'onboarding.questionnaire.complete': 'Complete Onboarding',
    'onboarding.completed.title': 'Onboarding Completed',
    'onboarding.completed.description': 'Your clinic setup is complete. You can now start using Nexi.',
    'onboarding.completed.success': 'Onboarding completed successfully.',
    'onboarding.error.missingToken': 'Missing onboarding token.',
    'onboarding.error.invalidToken': 'Invalid or expired onboarding token.',
    'onboarding.error.invalidEmail': 'Please provide a valid email address.',
    'onboarding.error.passwordLength': 'Password must be at least 6 characters long.',
    'onboarding.error.sdkNotReady': 'Facebook SDK is not ready.',
    'onboarding.error.embeddedCancelled': 'Embedded signup was cancelled or did not return a code.',
    'onboarding.error.requiredFields': 'Please complete all required fields.',
    'onboarding.error.disclosePrice': 'Please specify whether you disclose prices.',
    'onboarding.error.serviceCost': 'Please provide your service cost details.',
    'onboarding.error.aiConsent': 'Please specify AI training consent.',
    'onboarding.admin.created': 'Admin account created.',

    // Appointment Reminder Settings
    'appointmentReminderSettings.title': 'Appointment Reminder Settings',
    'appointmentReminderSettings.subtitle': 'Manage appointment reminder settings for your clinic',
    'appointmentReminderSettings.addNew': 'Add New Setting',
    'appointmentReminderSettings.filters': 'Filters',
    'appointmentReminderSettings.location': 'Location',
    'appointmentReminderSettings.doctor': 'Doctor',
    'appointmentReminderSettings.enabledStatus': 'Enabled Status',
    'appointmentReminderSettings.allLocations': 'All Locations',
    'appointmentReminderSettings.allDoctors': 'All Doctors',
    'appointmentReminderSettings.all': 'All',
    'appointmentReminderSettings.enabled': 'Enabled',
    'appointmentReminderSettings.disabled': 'Disabled',
    'appointmentReminderSettings.clearFilters': 'Clear Filters',
    'appointmentReminderSettings.editSetting': 'Edit Setting',
    'appointmentReminderSettings.addNewSetting': 'Add New Setting',
    'appointmentReminderSettings.timezone': 'Timezone',
    'appointmentReminderSettings.allLocationsNoSpecific': 'All Locations (No specific location)',
    'appointmentReminderSettings.allDoctorsNoSpecific': 'All Doctors (No specific doctor)',
    'appointmentReminderSettings.reminderDaysBefore': 'Reminder Days Before',
    'appointmentReminderSettings.reminderDaysBeforeHint': 'Number of days before appointment to send reminder',
    'appointmentReminderSettings.cutoffReminderAt': 'Cutoff Reminder At',
    'appointmentReminderSettings.cutoffReminderAtHint': 'Time cutoff for sending reminders (HH:MM format)',
    'appointmentReminderSettings.setting': 'Setting',
    'appointmentReminderSettings.disable': 'Disable',
    'appointmentReminderSettings.enable': 'Enable',
    'appointmentReminderSettings.daysBefore': 'Days Before',
    'appointmentReminderSettings.cutoffTime': 'Cutoff Time',
    'appointmentReminderSettings.noSettings': 'No appointment reminder settings found.',
    'appointmentReminderSettings.error.loadFailed': 'Failed to load appointment reminder settings',
    'appointmentReminderSettings.error.saveFailed': 'Failed to save appointment reminder setting',
    'appointmentReminderSettings.error.deleteFailed': 'Failed to delete appointment reminder setting',
    'appointmentReminderSettings.error.toggleFailed': 'Failed to toggle appointment reminder setting',
    'appointmentReminderSettings.error.invalidTimeFormat': 'Cutoff reminder time must be in HH:MM format (e.g., 17:00)',
    'appointmentReminderSettings.success.created': 'Appointment reminder setting created successfully',
    'appointmentReminderSettings.success.updated': 'Appointment reminder setting updated successfully',
    'appointmentReminderSettings.success.deleted': 'Appointment reminder setting deleted successfully',
    'appointmentReminderSettings.success.toggled': 'Appointment reminder setting status toggled successfully',
    'appointmentReminderSettings.confirm.delete': 'Are you sure you want to delete this appointment reminder setting?',
    'appointmentReminderSettings.notAvailable': 'N/A',

    // Import
    'import.andMore': '... and {{count}} more'
  },
  zh_HK: {
    // 登入頁面
    'login.title': '登入您的帳戶',
    'login.subtitle': '存取 Nexi 儀表板',
    'login.traditional': '傳統登入',
    'login.phone': '手機號碼登入',
    'login.username': '用戶名',
    'login.usernameOrEmail': '用戶名或電郵',
    'login.password': '密碼',
    'login.clinicId': '診所ID',
    'login.clinicIdPlaceholder': '診所ID',
    'login.signIn': '登入',
    'login.signingIn': '登入中...',
    'login.notRegisteredHint': '尚未註冊？請使用已訂閱的手機號碼',
    'login.loginHere': '在此登入',

    // 手機登入
    'phoneLogin.phoneNumber': '手機號碼',
    'phoneLogin.phoneNumberPlaceholder': '請輸入手機號碼 (例如: +85212345678)',
    'phoneLogin.queryClinics': '查詢診所',
    'phoneLogin.querying': '查詢中...',
    'phoneLogin.selectClinic': '選擇診所',
    'phoneLogin.back': '返回',
    'phoneLogin.sendCode': '發送驗證碼',
    'phoneLogin.sending': '發送中...',
    'phoneLogin.verificationCode': '驗證碼',
    'phoneLogin.codeSentTo': '驗證碼已發送至',
    'phoneLogin.enterCode': '請輸入6位數驗證碼',
    'phoneLogin.verifyAndLogin': '驗證並登入',
    'phoneLogin.verifying': '驗證中...',

    // 錯誤訊息
    'error.phoneRequired': '請輸入手機號碼',
    'error.clinicRequired': '請選擇診所',
    'error.codeRequired': '請輸入驗證碼',
    'error.clinicsNotFound': '查詢診所失敗，請檢查手機號碼',
    'error.sendCodeFailed': '發送驗證碼失敗',
    'error.codeInvalid': '驗證碼錯誤或已過期',

    // 集中化錯誤代碼
    'error.usernameAlreadyExists': '用戶名已存在',
    'error.phoneNumberNotAvailable': '手機號碼不可用',
    'error.noClinicsFoundForThisPhoneNumber': '此手機號碼未找到診所',
    'error.failedToSendVerificationCode': '發送驗證碼失敗',
    'error.passwordTooShort': '密碼必須至少6個字符',
    'error.incorrectUsernameOrPassword': '用戶名或密碼錯誤',
    'error.emailAlreadyRegistered': '電子郵件已註冊',
    'error.phoneNumberNotAssociatedWithThisClinic': '手機號碼與此診所無關聯',
    'error.clinicNotFound': '診所未找到',
    'error.invalidOrExpiredVerificationCode': '驗證碼無效或已過期',
    'error.currentPasswordIncorrect': '目前密碼錯誤',
    'error.twoFactorAuthOnlyForTempUsers': '2FA密碼更新僅適用於臨時用戶',
    'error.phoneNumberNotAvailableForVerification': '手機號碼不可用於驗證',
    'error.newPasswordTooShort': '新密碼必須至少6個字符',
    'error.passwordsMustBeDifferent': '新密碼必須與目前密碼不同',
    'error.twoFactorAuthOnlyForRegularUsers': '2FA用戶名更新僅適用於普通用戶',
    'error.newUsernameTooShort': '新用戶名必須至少3個字符',
    'error.usernamesMustBeDifferent': '新用戶名必須與目前用戶名不同',
    'error.verificationCodeNotFound': '驗證碼未找到或已過期',
    'error.invalidVerificationCode': '驗證碼無效',
    'error.conversionOnlyForTempUsers': '帳戶轉換僅適用於臨時用戶',
    'error.invalidEmailFormat': '電子郵件格式無效',
    'error.tempUsersShouldUseTwoFactorAuth': '臨時用戶應使用2FA密碼更新端點',
    'error.usernameRequired': '用戶名是必需的',
    'error.emailRequired': '電子郵件是必需的',

    // 導航
    'nav.dashboard': '儀表板',
    'nav.appointment': '預約',
    'nav.blockSchedule': '假期',
    'nav.patients': '病人',
    'nav.doctors': '醫生',
    'nav.locations': '診所位置',
    'nav.qa': '問答知識庫',
    'nav.voicemail': '語音信箱',
    'nav.callLogs': '來電記錄',
    'nav.appointmentReminderJobs': '預約提醒任務',
    'nav.appointmentReminderSettings': '預約提醒設定',
    'nav.conversations': '對話',
    'nav.conversationActivities': '對話活動',
    'nav.media': '媒體',
    'nav.import': '數據導入',
    'nav.subscriptions': '訂閱管理',
    'nav.analytics': '分析',
    'nav.toolbox': '工具箱',
    'nav.metaWhatsAppTemplates': 'WhatsApp 模板',
    'nav.embeddedSignup': '嵌入式接入',
    'nav.settings': '設置',
    'nav.users': '用戶管理',
    'nav.signOut': '登出',

    // 儀表板
    'dashboard.title': '儀表板',
    'dashboard.welcome': '歡迎回來',

    // 來電記錄
    'callLogs.title': '來電記錄',
    'callLogs.subtitle': '查看所有未接來電短信回覆',
    'callLogs.search': '搜索來電記錄...',
    'callLogs.noResults': '沒有找到來電記錄',
    'callLogs.loading': '正在加載來電記錄...',
    'callLogs.error': '加載來電記錄時出錯',
    'callLogs.fromNumber': '來自',
    'callLogs.toNumber': '發送至',
    'callLogs.timestamp': '時間',
    'callLogs.message': '消息',
    'callLogs.channel': '渠道',
    'callLogs.customer': '客戶',
    'callLogs.totalCalls': '總通話數',
    'callLogs.todayCalls': '今日通話數',
    'callLogs.dateFrom': '開始日期',
    'callLogs.dateTo': '結束日期',
    'callLogs.filter': '篩選',
    'callLogs.clear': '清除',
    'callLogs.type': '類型',
    'callLogs.voicemail': '語音信箱',
    'callLogs.call': '通話',
    'callLogs.playVoicemail': '播放語音信箱',
    'callLogs.duration': '持續時間',
    'dashboard.callsToday': '今日通話',
    'dashboard.viewCallsToday': '查看今日通話',
    'dashboard.messagesInbound': '收到訊息',

    // 媒體
    'media.title': '媒體',
    'media.subtitle': '查看病人嘗試發送的所有媒體',
    'media.search': '搜尋媒體...',
    'media.noResults': '沒有找到媒體',
    'media.loading': '正在載入媒體...',
    'media.error': '載入媒體時出錯',
    'media.patientName': '病人',
    'media.fromNumber': '來自',
    'media.toNumber': '發送至',
    'media.mediaType': '類型',
    'media.timestamp': '時間',
    'media.channel': '渠道',
    'media.status': '狀態',
    'media.actions': '操作',
    'media.view': '查看',
    'media.download': '下載',
    'media.filterByType': '按類型篩選：',
    'media.allTypes': '所有類型',
    'media.image': '圖片',
    'media.audio': '音頻',
    'media.video': '視頻',
    'media.document': '文檔',
    'media.unknown': '未知',
    'media.dateFrom': '開始日期',
    'media.dateTo': '結束日期',
    'media.filter': '篩選',
    'media.clear': '清除',
    'media.totalMedia': '總媒體數',
    'media.todayMedia': '今日媒體',
    'dashboard.upcomingAppointments': '即將到來的預約',
    'dashboard.todayAppointments': '今日預約',
    'dashboard.recentActivity': '最近活動',
    'dashboard.loading': '正在載入儀表板數據...',

    // TODO 設置
    'setup.title': '設置進度',
    'setup.subtitle': '完成這些步驟以完全配置您的診所',
    'setup.progress': '整體進度',
    'setup.tasksCompleted': '任務已完成',
    'setup.complete': '設置完成！',
    'setup.completeMessage': '所有配置任務已完成。您的診所已準備就緒！',
    'setup.refreshStatus': '刷新狀態',
    'setup.goToDashboard': '前往儀表板',
    'setup.highPriority': '高優先級',
    'setup.mediumPriority': '中優先級',
    'setup.lowPriority': '低優先級',
    'setup.priority': '優先級',

    // 預約
    'appointment.title': '預約',
    'appointment.upcoming': '即將到來',
    'appointment.today': '今日',
    'appointment.past': '過去',
    'appointment.new': '新增預約',
    'appointment.edit': '編輯預約',
    'appointment.delete': '刪除預約',
    'appointment.confirm': '確認預約',
    'appointment.cancel': '取消預約',
    'appointment.patient': '病人',
    'appointment.doctor': '醫生',
    'appointment.date': '日期',
    'appointment.time': '時間',
    'appointment.status': '狀態',
    'appointment.notes': '備註',

    'edit': '修改',
    'delete': '刪除',
    'save': '儲存',
    'cancel': '取消',
    'add': '新增',
    'close': '關閉',
    'confirm': '確認',

    // 病人
    'patient.title': '病人',
    'patient.search': '搜尋病人...',
    'patient.name': '姓名',
    'patient.phone': '電話',
    'patient.email': '電子郵件',
    'patient.lastVisit': '最後就診',
    'patient.appointments': '預約',
    'patient.media': '媒體',
    'patient.details': '病人詳情',
    'patient.history': '病歷',
    'patient.patient': '病人',
    'patient.language': '語言',
    'patient.lastContact': '最後聯繫',
    'patient.viewDetails': '檢視詳情',
    'patient.languagePreference': '語言偏好',

    // 醫生
    'doctor.title': '醫生',
    'doctor.search': '搜尋醫生...',
    'doctor.name': '姓名',
    'doctor.specialization': '專科',
    'doctor.phone': '電話',
    'doctor.email': '電子郵件',
    'doctor.status': '狀態',
    'doctor.active': '啟用',
    'doctor.inactive': '停用',
    'doctor.add': '新增醫生',
    'doctor.edit': '編輯醫生',
    'doctor.delete': '刪除醫生',
    'doctor.save': '儲存醫生',
    'doctor.cancel': '取消',
    'doctor.male': '男',
    'doctor.female': '女',
    'doctor.other': '其他',
    'doctor.doctor': '醫生',
    'doctor.clinic': '診所',
    'doctor.facility': '設施',
    'doctor.gender': '性別',
    'doctor.type': '類型',
    'doctor.serviceProvided': '服務提供',
    'doctor.enterName': '請輸入醫生姓名',
    'doctor.enterSpecialization': '請輸入專科',
    'doctor.enterService': '請輸入服務提供',
    'doctor.selectGender': '請選擇性別',
    'doctor.fillRequired': '請填寫所有必填欄位。',
    'doctor.addSuccess': '醫生新增成功',
    'doctor.updateSuccess': '醫生更新成功',
    'doctor.deleteSuccess': '醫生刪除成功',
    'doctor.addError': '新增醫生失敗',
    'doctor.updateError': '更新醫生失敗',
    'doctor.deleteError': '刪除醫生失敗',
    'doctor.loadError': '載入醫生失敗，請稍後再試。',
    'doctor.deleteConfirm': '您確定要刪除此醫生嗎？',
    'doctor.saving': '儲存中...',
    'doctor.noData': '未找到醫生。請使用上方按鈕新增您的第一個醫生。',
    'doctor.moduleRequirement': '模組需求',
    'doctor.moduleDescription': '為獲得完整功能，此功能與結構化數據模組啟用時效果最佳。當此模組啟用時，醫生的資訊可以直接被AI助理存取。',

    // 診所位置
    'location.title': '診所位置',
    'location.subtitle': '管理診所位置',
    'location.addNew': '新增位置',
    'location.edit': '編輯位置',
    'location.delete': '刪除位置',
    'location.primaryName': '主要名稱',
    'location.externalCode': '外部位置代碼',
    'location.externalCodePlaceholder': '可選的外部代碼',
    'location.i18nNames': '國際化名稱（可選）',
    'location.i18nAddresses': '國際化地址（可選）',
    'location.name': '名稱',
    'location.address': '地址',
    'location.code': '代碼',
    'location.localizedNames': '本地化名稱',
    'location.localizedAddresses': '本地化地址',
    'location.loading': '載入位置中...',
    'location.loadError': '載入位置失敗，請稍後再試。',
    'location.fillRequired': '請填寫主要名稱。',
    'location.addSuccess': '位置新增成功',
    'location.addError': '新增位置失敗，請稍後再試。',
    'location.updateSuccess': '位置更新成功',
    'location.updateError': '更新位置失敗，請稍後再試。',
    'location.deleteSuccess': '位置刪除成功',
    'location.deleteError': '刪除位置失敗，請稍後再試。',
    'location.deleteConfirm': '您確定要刪除此位置嗎？',
    'location.noLocations': '未找到位置。請新增您的第一個位置以開始使用。',

    // 問答知識庫
    'qa.title': '問答知識庫',
    'qa.search': '搜尋問題或答案...',
    'qa.question': '問題',
    'qa.answer': '答案',
    'qa.language': '語言',
    'qa.status': '狀態',
    'qa.active': '啟用',
    'qa.inactive': '停用',
    'qa.add': '新增問答',
    'qa.edit': '編輯問答',
    'qa.delete': '刪除問答',
    'qa.save': '儲存問答',
    'qa.cancel': '取消',
    'qa.import': '從JSON導入',
    'qa.export': '導出為JSON',
    'qa.filter': '按語言篩選',
    'qa.all': '所有語言',
    'qa.addNewItem': '新增問答項目',
    'qa.editItem': '編輯問答項目',
    'qa.manageTags': '管理標籤',
    'qa.importing': '導入中...',
    'qa.filterByLanguage': '按語言篩選：',
    'qa.filterByTag': '按標籤篩選：',
    'qa.allTags': '所有標籤',
    'qa.searchPlaceholder': '搜尋問題或答案...',
    'qa.searching': '搜尋中...',
    'qa.noResults': '未找到問答知識庫項目。',
    'qa.noResultsFiltered': '未找到符合所選篩選條件的問答項目。',
    'qa.tryDifferentFilters': '請嘗試選擇不同的篩選條件。',
    'qa.clickToAddFirst': '點擊「新增問答項目」來建立第一個項目。',
    'qa.statusInfo': '狀態及資訊',
    'qa.actions': '操作',
    'qa.rule': '規則：',
    'qa.lang': '語言：',
    'qa.tags': '標籤：',
    'qa.deactivate': '停用',
    'qa.activate': '啟用',
    'qa.attachRule': '附加規則',
    'qa.attachRuleDescription': '問答項目應包含在以下情況：',
    'qa.attachRuleAlways': '總是：包含在所有對話中',
    'qa.attachRuleIfMatch': '語言匹配時：僅當對話語言與問答語言匹配時包含',
    'qa.attachRuleOnHold': '暫停：暫時不使用直至手動更改',
    'qa.attachRuleAlwaysLabel': '總是',
    'qa.attachRuleIfMatchLabel': '語言匹配時',
    'qa.attachRuleOnHoldLabel': '暫停',
    'qa.languageDescription': '選擇此問答項目的語言。',
    'qa.questionDescription': '病人可能會問的問題。',
    'qa.answerDescription': '將提供給問題的答案。',
    'qa.tagsDescription': '選擇標籤來分類此問答項目。按住 Ctrl/Cmd 選擇多個標籤。',
    'qa.enterQuestion': '輸入問題',
    'qa.enterAnswer': '輸入答案',
    'qa.saving': '儲存中...',
    'qa.update': '更新',
    'qa.itemDetails': '問答項目詳情',
    'qa.close': '關閉',
    'qa.tagManagement': '管理標籤',
    'qa.existingTags': '現有標籤',
    'qa.addNewTag': '新增標籤',
    'qa.tagName': '標籤名稱',
    'qa.tagColor': '顏色',
    'qa.enterTagName': '輸入標籤名稱',
    'qa.create': '建立',
    'qa.noTagsYet': '尚未建立標籤。點擊「新增標籤」建立您的第一個標籤。',
    'qa.deleteTagConfirm': '您確定要刪除此標籤嗎？這將從所有問答項目中移除它。',
    'qa.deleteItemConfirm': '您確定要刪除此問答知識庫項目嗎？',
    'qa.loadingItems': '正在載入問答知識庫...',
    'qa.validJsonRequired': '請上傳有效的JSON檔案',
    'qa.jsonArrayRequired': 'JSON檔案必須包含問答項目陣列',
    'qa.noValidItems': '檔案中未找到有效的問答項目',
    'qa.jsonParseError': '無法解析JSON檔案。請確保檔案是有效的JSON。',
    'qa.fileReadError': '無法讀取檔案',
    'qa.importFailed': '導入問答項目失敗',
    'qa.itemAddedSuccess': '問答知識庫項目新增成功',
    'qa.itemUpdatedSuccess': '問答知識庫項目更新成功',
    'qa.itemDeletedSuccess': '問答知識庫項目刪除成功',
    'qa.itemActivatedSuccess': '問答知識庫項目啟用成功',
    'qa.itemDeactivatedSuccess': '問答知識庫項目停用成功',
    'qa.itemSaveFailed': '儲存問答知識庫項目失敗',
    'qa.itemDeleteFailed': '刪除問答知識庫項目失敗',
    'qa.itemStatusUpdateFailed': '更新問答知識庫項目狀態失敗',
    'qa.tagCreatedSuccess': '標籤建立成功',
    'qa.tagUpdatedSuccess': '標籤更新成功',
    'qa.tagDeletedSuccess': '標籤刪除成功',
    'qa.tagSaveFailed': '儲存標籤失敗',
    'qa.tagDeleteFailed': '刪除標籤失敗',
    'qa.tagNameRequired': '標籤名稱為必填',
    'qa.questionAnswerRequired': '問題和答案欄位為必填',
    'qa.chineseTraditional': '繁體中文',
    "qa.exportJSON": "導出為JSON",
    "qa.exportHTML": "導出為HTML",
    "qa.exporting": "導出中...",
    "qa.exportSuccess": "導出成功",
    "qa.exportFailed": "導出失敗",
    "qa.exportHTMLSuccess": "導出為HTML成功",
    "qa.exportHTMLFailed": "導出為HTML失敗",
    'qa.chineseSimplified': '簡體中文',
    'qa.englishUS': '英語（美國）',
    'qa.importFromJson': '從JSON導入',
    'qa.addNewQaItem': '新增問答項目',
    'qa.allLanguages': '所有語言',
    'qa.searchQuestionsAnswers': '搜尋問題或答案...',
    'qa.editQaItem': '編輯問答項目',
    'qa.selectLanguageForQaItem': '選擇此問答項目的語言。',
    'qa.questionPatientsMightAsk': '病人可能會問的問題。',
    'qa.answerProvidedToQuestion': '提供答案。如答案較長，為確保AI助理的表現，請拆分為數個問答項目。',
    'qa.whenQaItemShouldBeIncluded': '問答項目應包含在以下情況',
    'qa.alwaysInclude': '總是：包含在所有對話中',
    'qa.ifLanguageMatchInclude': '語言匹配時：僅當對話語言與問答語言匹配時包含',
    'qa.onHoldDoNotUse': '暫停：暫時不使用直至手動更改',
    'qa.selectTagsToCategorizeQaItem': '選擇標籤來分類此問答項目',
    'qa.typeToSearchAndSelectTags': '輸入以搜尋並選擇標籤',
    'qa.holdCtrlCmdToSelectMultipleTags': '按住 Ctrl/Cmd 選擇多個標籤',
    'qa.noQaKnowledgeBaseItemsFound': '未找到問答知識庫項目。點擊「新增問答項目」來建立第一個項目。',
    'qa.noQaItemsFoundForSelectedFilters': '未找到符合所選篩選條件的問答項目',
    'qa.trySelectingDifferentFilters': '請嘗試選擇不同的篩選條件。',
    'qa.qaItemDetails': '問答項目詳情',
    'qa.color': '顏色',
    'qa.noTagsCreatedYet': '尚未建立標籤。點擊「新增標籤」建立您的第一個標籤。',
    'qa.loadingItemsFailed': '載入問答知識庫項目失敗',

    // 語音信箱
    'voicemail.title': '語音信箱',
    'voicemail.search': '搜尋錄音...',

    // 通話錄音
    'recordings.title': '電話錄音',
    'recordings.search.placeholder': '搜尋電話號碼或錄音內容...',
    'recordings.filter.all': '所有錄音',
    'recordings.filter.processed': '已處理',
    'recordings.filter.pending': '待處理',
    'recordings.filter.skipped': '已跳過',
    'recordings.showing': '顯示第 {{start}} 至 {{end}} 項，共 {{total}} 項錄音',
    'recordings.noResults': '找不到符合條件的錄音',
    'recordings.noRecordings': '找不到電話錄音',
    'recordings.status.processed': '已處理',
    'recordings.status.pending': '待處理',
    'recordings.status.skipped': '已跳過',
    'recordings.table.caller': '來電者',
    'recordings.table.recipient': '接收者',
    'recordings.table.transcript': '錄音內容',
    'recordings.table.date': '日期',
    'recordings.table.duration': '時長',
    'recordings.table.status': '狀態',
    'recordings.table.actions': '操作',
    'recordings.actions.view': '查看',
    'recordings.actions.listen': '播放',
    'recordings.noTranscript': '暫無錄音內容',
    'recordings.noDuration': '未知',
    'recordings.detail.title': '電話錄音詳情',
    'recordings.detail.backToRecordings': '返回錄音列表',
    'recordings.detail.notFound': '找不到錄音',
    'recordings.detail.caller': '來電者',
    'recordings.detail.recipient': '接收者',
    'recordings.detail.date': '日期',
    'recordings.detail.time': '時間',
    'recordings.detail.duration': '時長',
    'recordings.detail.conversationId': '對話ID',
    'recordings.detail.source': '來源',
    'recordings.detail.audioRecording': '錄音檔案',
    'recordings.detail.audioNotSupported': '您的瀏覽器不支援音頻播放。',
    'recordings.detail.downloadAudio': '下載錄音檔案',

    // 語音信箱
    'voiceMail.title': '語音信箱轉錄',
    'voiceMail.transcription': '轉錄內容',
    'voiceMail.audioRecording': '錄音檔案',
    'voiceMail.audioNotSupported': '您的瀏覽器不支援音頻播放。',
    'voiceMail.downloadAudio': '錄音檔案',
    'voiceMail.duration': '時長',
    'voiceMail.noDuration': '未知',
    'voiceMail.minutes': '分鐘',
    'voiceMail.seconds': '秒',
    'recordings.detail.transcript': '錄音內容',
    'recordings.detail.editTranscript': '編輯',
    'recordings.detail.expand': '展開',
    'recordings.detail.collapse': '收起',
    'recordings.detail.save': '儲存',
    'recordings.detail.saving': '儲存中...',
    'recordings.detail.cancel': '取消',
    'recordings.detail.transcriptPlaceholder': '請輸入錄音內容...',
    'recordings.detail.noTranscript': '暫無錄音內容',
    'recordings.detail.noDuration': '未知',
    'voicemail.date': '日期',
    'voicemail.duration': '持續時間',
    'voicemail.caller': '來電者',
    'voicemail.status': '狀態',
    'voicemail.play': '播放',
    'voicemail.download': '下載',
    'voicemail.delete': '刪除',

    // 數據導入
    'import.title': '數據導入',
    'import.selectFile': '選擇檔案',
    'import.upload': '上傳',
    'import.processing': '處理中...',
    'import.success': '導入成功',
    'import.error': '導入失敗',
    'import.format': '支援格式：Excel (.xlsx)',
    'import.appointmentReminder': '預約提醒',
    'import.appointmentReminderDescription': '透過 WhatsApp 向病人發送預約提醒',
    'import.invalidFileType': '請選擇有效的 Excel (.xlsx) 檔案',
    'import.downloadFailed': '下載範本失敗。請重試或聯絡支援。',
    'import.selectFileFirst': '請先選擇要上傳的檔案',
    'import.uploadError': '上傳檔案時發生錯誤',
    'import.processingError': '處理導入時發生錯誤',
    'import.step1': '上傳檔案',
    'import.step2': '檢視數據',
    'import.step3': '處理數據',
    'import.importType': '導入類型',
    'import.template': '範本',
    'import.downloadTemplate': '下載範本範例',
    'import.templateDescription': '下載範本檔案以查看所需格式',
    'import.uploadExcelFile': '上傳 Excel 檔案',
    'import.uploading': '上傳中...',
    'import.uploadAndPreview': '上傳並預覽',
    'import.importedDataPreview': '導入數據預覽',
    'import.filename': '檔案名稱',
    'import.records': '記錄數',
    'import.processData': '處理數據',
    'import.processingComplete': '導入處理完成',
    'import.startNewImport': '開始新的導入',

    // 設置
    'settings.title': '帳戶設置',
    'settings.subtitle': '管理您的帳戶偏好設置和安全設置',
    'settings.currentInfo': '目前帳戶資訊',
    'settings.username': '用戶名',
    'settings.email': '電子郵件',
    'settings.clinicId': '診所ID',
    'settings.updateUsername': '更新用戶名',
    'settings.newUsername': '新用戶名',
    'settings.enterNewUsername': '輸入新用戶名',
    'settings.currentPassword': '目前密碼',
    'settings.enterCurrentPassword': '輸入目前密碼',
    'settings.updatePassword': '更新密碼',
    'settings.newPassword': '新密碼',
    'settings.enterNewPassword': '輸入新密碼',
    'settings.confirmPassword': '確認新密碼',
    'settings.confirmNewPassword': '確認新密碼',

    // 設置錯誤訊息
    'error.allFieldsRequired': '所有欄位都是必填的',
    'error.usernameUnchanged': '新用戶名必須與目前用戶名不同',
    'error.passwordMismatch': '新密碼不相符',
    'error.passwordUnchanged': '新密碼必須與目前密碼不同',
    'error.updateFailed': '更新失敗',

    // 設置2FA訊息
    'settings.2faNote': '為了安全起見，我們會向您的手機號碼發送驗證碼',
    'settings.tempUserPasswordNote': '為了安全起見，我們會向您的手機號碼發送驗證碼以確認密碼更改。',
    'settings.completeProfile': '完善您的個人資料',
    'settings.tempUserMessage': '請設置您的帳戶用戶名、電子郵件和密碼以繼續。',
    'settings.phoneNumber': '手機號碼',
    'settings.password': '密碼',
    'settings.enterUsername': '輸入您的用戶名',
    'settings.enterEmail': '輸入您的電子郵件地址',
    'settings.enterPassword': '輸入您的密碼',
    'settings.completeSetup': '完成帳戶設置',
    'error.invalidEmail': '請輸入有效的電子郵件地址',
    'error.conversionFailed': '帳戶轉換失敗',
    'common.processing': '處理中...',
    'settings.verifyUpdate': '驗證並更新',

    // 通用
    'common.language': '語言',
    'common.search': '搜尋',
    'common.filter': '篩選',
    'common.sort': '排序',
    'common.loading': '載入中...',
    'common.save': '儲存',
    'common.cancel': '取消',
    'common.delete': '刪除',
    'common.edit': '編輯',
    'common.add': '新增',
    'common.close': '關閉',
    'common.confirm': '確認',
    'common.yes': '是',
    'common.no': '否',
    'common.ok': '確定',
    'common.error': '錯誤',
    'common.success': '成功',
    'common.warning': '警告',
    'common.info': '資訊',
    'common.next': '下一個',
    'common.previous': '上一個',
    'common.first': '第一個',
    'common.last': '最後一個',
    'common.page': '頁面',
    'common.of': '的',
    'common.items': '項目',
    'common.showing': '顯示',
    'common.to': '至',
    'common.noData': '沒有可用數據',
    'common.selectAll': '全選',
    'common.deselectAll': '取消全選',
    'common.actions': '操作',
    'common.back': '返回',
    'common.updating': '更新中...',
    'common.retry': '重試',

    // 對話活動
    'conversationActivities.title': '對話活動',
    'conversationActivities.subtitle': '查看所有對話活動和事件',
    'conversationActivities.search': '按對話ID或訊息內容搜尋...',
    'conversationActivities.noResults': '沒有找到活動',
    'conversationActivities.loading': '正在載入活動...',
    'conversationActivities.error': '載入活動時出錯',
    'conversationActivities.conversationId': '對話ID',
    'conversationActivities.eventType': '事件類型',
    'conversationActivities.timestamp': '時間戳',
    'conversationActivities.customer': '客戶',
    'conversationActivities.channel': '渠道',
    'conversationActivities.seen': '已查看',
    'conversationActivities.unseen': '未查看',

    // 用戶管理
    'users.title': '用戶管理',
    'users.create': '創建用戶',
    'users.edit': '編輯用戶',
    'users.update': '更新用戶',
    'users.accessDenied': '訪問被拒絕',
    'users.adminOnly': '只有管理員用戶才能訪問此頁面',
    'users.role.admin': '管理員',
    'users.role.manager': '經理',
    'users.role.user': '用戶',
    'users.role.viewer': '檢視者',
    'users.status.active': '啟用',
    'users.status.inactive': '停用',
    'users.never': '從未',
    'users.actions.edit': '編輯',
    'users.actions.deactivate': '停用',
    'users.actions.generatePassword': '生成',
    'users.actions.copyPassword': '複製密碼',
    'users.actions.showPassword': '顯示密碼',
    'users.actions.hidePassword': '隱藏密碼',
    'users.form.username': '用戶名',
    'users.form.email': '電子郵件',
    'users.form.password': '密碼',
    'users.form.passwordPlaceholder': '輸入密碼或生成一個',
    'users.form.passwordGenerated': '密碼已生成',
    'users.form.characters': '字符',
    'users.form.role': '角色',
    'users.form.phoneNumber': '手機號碼',
    'users.table.user': '用戶',
    'users.table.email': '電子郵件',
    'users.table.role': '角色',
    'users.table.status': '狀態',
    'users.table.lastLogin': '最後登入',
    'users.table.actions': '操作',
    'users.success.created': '用戶創建成功',
    'users.success.updated': '用戶更新成功',
    'users.success.deactivated': '用戶停用成功',
    'users.success.passwordGenerated': '密碼生成成功',
    'users.success.passwordCopied': '密碼已複製到剪貼板',
    'users.error.fetchFailed': '獲取用戶失敗',
    'users.error.createFailed': '創建用戶失敗',
    'users.error.updateFailed': '更新用戶失敗',
    'users.error.deactivateFailed': '停用用戶失敗',
    'users.error.copyFailed': '複製密碼到剪貼板失敗',
    'users.confirm.deactivate': '您確定要停用此用戶嗎？',
    'conversationActivities.showUnseenOnly': '僅顯示未查看',
    'conversationActivities.groupByConversation': '按對話分組',
    'conversationActivities.dateFrom': '開始日期',
    'conversationActivities.dateTo': '結束日期',
    'conversationActivities.filter': '篩選',
    'conversationActivities.clear': '清除',
    'conversationActivities.totalActivities': '總活動數',
    'conversationActivities.todayActivities': '今日活動',
    'conversationActivities.itemsPerPage': '每頁項目數',
    'conversationActivities.last7Days': '最近7天',
    'conversationActivities.last30Days': '最近30天',
    'conversationActivities.conversationIdFilter': '對話ID篩選',
    'conversationActivities.filteredByConversationId': '按對話ID篩選...',
    'conversationActivities.clearFilter': '清除篩選',
    'conversationActivities.selected': '已選擇',
    'conversationActivities.setSeenForSelected': '將選中的設為已查看',
    'conversationActivities.dateFilter': '日期篩選',
    'conversationActivities.quickFilters': '快速篩選',
    'conversationActivities.clearAllFilters': '清除所有篩選',

    // 預約事件值
    'appointment.doctor': '醫生',
    'appointment.date': '日期',
    'appointment.time': '時間',
    'appointment.remarks': '備註',
    'appointment.notAvailable': '不適用',

    // 預約資訊事件值
    'appointmentInfo.submitForm': '提交表單',
    'appointmentInfo.updateInfo': '預約資訊更新',
    'appointmentInfo.appointmentInfo': '預約資訊',
    'appointmentInfo.patientData': '病人資料',
    'appointmentInfo.name': '姓名',
    'appointmentInfo.dateOfBirth': '出生日期',
    'appointmentInfo.gender': '性別',
    'appointmentInfo.phone': '電話',
    'appointmentInfo.email': '電郵',
    'appointmentInfo.language': '語言',
    'appointmentInfo.patientType': '病人類型',
    'appointmentInfo.preferences': '預約偏好',
    'appointmentInfo.preferredLocation': '偏好地點',
    'appointmentInfo.preferredDoctor': '偏好醫生',
    'appointmentInfo.preferredDoctor2': '偏好醫生 (2)',
    'appointmentInfo.preferredDoctor3': '偏好醫生 (3)',
    'appointmentInfo.preferredDate': '偏好日期',
    'appointmentInfo.preferredTime': '偏好時間',
    'appointmentInfo.additionalInfo': '額外資訊',
    'appointmentInfo.symptoms': '症狀',
    'appointmentInfo.specialRequest': '特殊要求',
    'appointmentInfo.bookingType': '預約類型',
    'appointmentInfo.systemInfo': '系統資訊',
    'appointmentInfo.appointmentInfoId': '預約資訊ID',

    // 預約建議事件值
    'appointmentOffer.title': '預約建議',
    'appointmentOffer.doctor': '醫生',
    'appointmentOffer.specialty': '專科',
    'appointmentOffer.date': '日期',
    'appointmentOffer.time': '時間',
    'appointmentOffer.location': '地點',
    'appointmentOffer.systemInfo': '系統資訊',
    'appointmentOffer.appointmentInfoId': '預約資訊ID',
    'appointmentOffer.appointmentOfferId': '預約建議ID',

    // 預約失敗事件值
    'failedAppointment.title': '預約失敗',
    'failedAppointment.customerName': '客戶姓名',
    'failedAppointment.notAvailable': '不適用',
    'failedAppointment.error': '錯誤',
    'failedAppointment.unknownError': '未知錯誤',
    'failedAppointment.rawErrorInfo': '原始錯誤資訊',
    'failedAppointment.technicalDetails': '技術詳情',
    'failedAppointment.errorCode': '錯誤代碼',
    'failedAppointment.errorCategory': '錯誤類別',
    'failedAppointment.systemInfo': '系統資訊',
    'failedAppointment.confirmedEventId': '預約確認ID',
    'failedAppointment.appointmentInfoId': '預約資訊ID',
    'failedAppointment.appointmentOfferId': '預約建議ID',

    // 建立預約事件值
    'createAppointment.title': '建立預約',
    'createAppointment.doctor': '醫生',
    'createAppointment.date': '日期',
    'createAppointment.time': '時間',
    'createAppointment.location': '地點',
    'createAppointment.remarks': '備註',
    'createAppointment.notAvailable': '不適用',
    'createAppointment.systemInfo': '系統資訊',
    'createAppointment.confirmedEventId': '預約確認ID',
    'createAppointment.appointmentInfoId': '預約資訊ID',
    'createAppointment.appointmentOfferId': '預約建議ID',

    // 確認預約建議事件值
    'confirmAppointment.title': '確認預約時間',
    'confirmAppointment.status': '狀態',
    'confirmAppointment.confirmed': '已確認',
    'confirmAppointment.cancelled': '已取消',
    'confirmAppointment.pending': '等待中',
    'confirmAppointment.unknownStatus': '未知狀態',
    'confirmAppointment.doctor': '醫生',
    'confirmAppointment.date': '日期',
    'confirmAppointment.time': '時間',
    'confirmAppointment.location': '地點',
    'confirmAppointment.systemInfo': '系統資訊',
    'confirmAppointment.confirmedEventId': '預約確認ID',
    'confirmAppointment.appointmentInfoId': '預約資訊ID',
    'confirmAppointment.appointmentOfferId': '預約建議ID',

    // 事件類型
    'eventType.clinicNotification': '診所通知',
    'eventType.leaveMessage': '留言',
    'eventType.createAppointment': '建立預約',
    'eventType.noMessageContent': '沒有訊息內容',
    'eventType.jsonData': 'JSON 資料',
    'eventType.viewJson': '查看 JSON',
    'eventType.createAppointmentOffer': '建議預約時間',
    'eventType.confirmAppointmentOffer': '確認預約時間',
    'eventType.submitForm': '提交表單',
    'eventType.updateAppointmentInfo': '更新預約資訊',
    'eventType.failedAppointment': '預約失敗',
    'eventType.transcribeVoiceMail': '語音信箱轉錄',
    'eventType.mediaUpload': '媒體上傳',
    'eventType.escalateMessage': '團隊升級',

    // 團隊升級
    'escalateMessage.title': '團隊升級',
    'escalateMessage.requestId': '請求編號',
    'escalateMessage.receivedAt': '收到時間',
    'escalateMessage.teamsNotified': '通知團隊',
    'escalateMessage.customerWhatsApp': '客戶 WhatsApp',
    'escalateMessage.openConversation': '開啟 WhatsApp 對話',
    'escalateMessage.recipients': '收件人',
    'escalateMessage.notAvailable': '不適用',
    'escalateMessage.severity.low': '低',
    'escalateMessage.severity.medium': '中',
    'escalateMessage.severity.high': '高',
    'escalateMessage.severity.critical': '緊急',
    'escalateMessage.team.subscription_list': '訂閱名單',

    // 對話活動頁面
    'conversationActivities.loadingActivities': '正在載入活動...',
    'conversationActivities.noActivitiesFound': '沒有找到對話活動。',
    'conversationActivities.conversationActivities': '對話活動',
    'conversationActivities.total': '總計',
    'conversationActivities.conversationGroup': '對話群組',
    'conversationActivities.events': '事件',
    'conversationActivities.expandAll': '展開全部',
    'conversationActivities.collapseAll': '收起全部',
    'conversationActivities.expandGroup': '展開群組',
    'conversationActivities.collapseGroup': '收起群組',
    'conversationActivities.displayed': '顯示中',
    'conversationActivities.selectGroup': '選擇群組',
    'conversationActivities.deselectGroup': '取消選擇群組',
    'conversationActivities.viewPatientDetails': '查看患者詳情',
    'conversationActivities.timestamp': '時間戳',
    'conversationActivities.eventType': '事件類型',
    'conversationActivities.phoneNumber': '電話號碼',
    'conversationActivities.message': '訊息',
    'conversationActivities.actions': '操作',
    'conversationActivities.clickToFilterByConversation': '點擊以按此對話篩選',
    'conversationActivities.seen': '已查看',
    'conversationActivities.setSeen': '設為已查看',
    'conversationActivities.internalRemark': '內部備註',
    'conversationActivities.addInternalRemark': '新增備註',
    'conversationActivities.editInternalRemark': '編輯備註',
    'conversationActivities.internalRemarkPlaceholder': '輸入內部備註...',
    'conversationActivities.save': '儲存',
    'conversationActivities.cancel': '取消',
    'conversationActivities.saving': '儲存中...',
    'conversationActivities.setting': '設定中...',
    'conversationActivities.showingPage': '顯示第',
    'conversationActivities.of': '頁，共',
    'conversationActivities.totalItems': '個項目',
    'conversationActivities.previous': '上一頁',
    'conversationActivities.next': '下一頁',
    'conversationActivities.whatsapp': 'WhatsApp',
    'conversationActivities.openWhatsApp': '開啟 WhatsApp 聊天',
    'conversationActivities.noPhoneNumber': '沒有可用的電話號碼',

    // Calendar specific translations
    'calendar.system': '系統',
    'calendar.loadAppointmentsFailed': '載入預約列表失敗，請稍後再試',
    'calendar.pendingConfirmation': '待確認',
    'calendar.clinicRequest': '診所請求',
    'calendar.minutes': '分鐘',
    'calendar.doctorUnavailable': '此時段醫生不可用',
    'calendar.appointmentUpdatedConfirmed': '預約已更新並確認',
    'calendar.saveAppointmentFailed': '保存預約失敗，請稍後再試',
    'calendar.appointmentConfirmed': '預約已確認',
    'calendar.confirmAppointmentFailed': '確認預約失敗，請稍後再試',
    'calendar.appointmentCancelled': '預約已取消',
    'calendar.cancelAppointmentFailed': '取消預約失敗，請稍後再試',
    'calendar.pleaseSelectDoctor': '請選擇醫生',
    'calendar.pleaseSelectDateRange': '請選擇日期範圍',
    'calendar.scheduleUpdated': '排班已更新',
    'calendar.scheduleCreated': '排班已創建',
    'calendar.saveScheduleFailed': '保存排班失敗，請稍後再試',
    'calendar.loadScheduleFailed': '載入排班資料失敗，請稍後再試',
    'calendar.confirmDeleteSchedule': '確定要刪除此排班嗎？',
    'calendar.scheduleDeleted': '排班已刪除',
    'calendar.deleteScheduleFailed': '刪除排班失敗，請稍後再試',
    'calendar.blockScheduleUpdated': '假期更新成功',
    'calendar.blockScheduleCreated': '假期創建成功',
    'calendar.blockScheduleDeleted': '假期刪除成功',
    'calendar.saveBlockScheduleFailed': '保存假期失敗',
    'calendar.loadBlockScheduleFailed': '載入假期失敗',
    'calendar.deleteBlockScheduleFailed': '刪除假期失敗',
    'calendar.confirmDeleteBlockSchedule': '確定要刪除此假期嗎？',
    'calendar.pleaseEnterReason': '請輸入原因',
    'calendar.pleaseSelectDateTimeRange': '請選擇日期和時間範圍',
    'calendar.timeSlotBlocked': '此時間段已被封鎖',
    'calendar.blockedSlots': '封鎖時間段',
    'calendar.today': '今天',
    'calendar.days': ['一', '二', '三', '四', '五', '六', '日'],
    'calendar.search': '搜尋',
    'calendar.searchAppointments': '搜尋預約...',
    'calendar.doctors': '醫生',
    'calendar.viewMore': '查看更多...',
    'calendar.hideCalendar': '隱藏日曆',
    'calendar.showCalendar': '顯示日曆',
    'calendar.manageSchedule': '管理排班',
    'calendar.manageBlockSchedule': '管理假期',
    'calendar.appointments': '預約',
    'calendar.noAppointments': '沒有預約',
    'calendar.addAppointment': '新增預約',
    'calendar.appointmentRequestSummary': '預約請求摘要',
    'calendar.confirmed': '已確認',
    'calendar.cancelled': '已取消',
    'calendar.unknown': '未知',
    'calendar.moreAppointments': '個預約...',
    'calendar.outOfHoursWarning': '在營業時間以外的預約。',
    'calendar.availableSlots': '可預約時段',
    'calendar.unavailableSlots': '不可預約時段',
    'calendar.doctorScheduleManagement': '醫生排班管理',
    'calendar.blockScheduleManagement': '假期管理',
    'calendar.selectDoctor': '選擇醫生',
    'calendar.pleaseSelectDoctor': '請選擇醫生',
    'calendar.addSchedule': '新增排班',
    'calendar.addBlockSchedule': '新增假期',
    'calendar.close': '關閉',
    'calendar.editAppointment': '編輯預約',
    'calendar.newAppointment': '新預約',
    'calendar.cancel': '取消',
    'calendar.title': '標題',
    'calendar.customerName': '客戶姓名',
    'calendar.enterCustomerName': '請輸入客戶姓名',
    'calendar.date': '日期',
    'calendar.time': '時間',
    'calendar.doctor': '醫生',
    'calendar.selectDoctor': '選擇醫生',
    'calendar.duration': '持續時間 (分鐘)',
    'calendar.remarks': '備註',
    'calendar.enterRemarks': '請輸入備註 (可選)',
    'calendar.processing': '處理中...',
    'calendar.updateAppointment': '更新預約',
    'calendar.submitAppointment': '提交預約',
    'calendar.appointmentActions': '預約操作',
    'calendar.contactPhone': '聯絡電話:',
    'calendar.appointmentDate': '日期:',
    'calendar.appointmentTime': '時間:',
    'calendar.appointmentDoctor': '醫生:',
    'calendar.appointmentStatus': '狀態:',
    'calendar.appointmentRemarks': '備註:',
    'calendar.cancelAppointment': '取消預約',
    'calendar.confirmAppointment': '確認預約',
    'calendar.editAndConfirm': '編輯並確認',
    'calendar.editAppointment': '編輯預約',

    // Schedule Dialog
    'scheduleDialog.editSchedule': '編輯排班',
    'scheduleDialog.addSchedule': '新增排班',
    'scheduleDialog.selectDoctor': '選擇醫生',
    'scheduleDialog.pleaseSelectDoctor': '請選擇醫生',
    'scheduleDialog.defaultSchedule': '預設排班',
    'scheduleDialog.setAsDefault': '設為預設排班',
    'scheduleDialog.startDate': '開始日期',
    'scheduleDialog.endDate': '結束日期',
    'scheduleDialog.defaultScheduleNoDateRange': '預設排班不需要日期範圍',
    'scheduleDialog.weeklySchedule': '每週排班',
    'scheduleDialog.monday': '週一',
    'scheduleDialog.tuesday': '週二',
    'scheduleDialog.wednesday': '週三',
    'scheduleDialog.thursday': '週四',
    'scheduleDialog.friday': '週五',
    'scheduleDialog.saturday': '週六',
    'scheduleDialog.sunday': '週日',
    'scheduleDialog.applyToAllDays': '套用到所有日期',
    'scheduleDialog.sortTimeSlots': '排序時段',
    'scheduleDialog.addTimeSlot': '添加時段',
    'scheduleDialog.startTime': '開始時間',
    'scheduleDialog.endTime': '結束時間',
    'scheduleDialog.removeTimeSlot': '移除時段',
    'scheduleDialog.noScheduleForDay': '此日無排班',
    'scheduleDialog.updateSchedule': '更新排班',
    'scheduleDialog.createSchedule': '創建排班',
    'scheduleDialog.confirmApplyToAllDays': '確定要將此日排班設定應用於所有日期嗎？',

    // Schedule List
    'scheduleList.noSchedules': '尚未建立排班。請點擊"新增排班"按鈕創建排班。',
    'scheduleList.establishedSchedules': '已建立的排班',
    'scheduleList.scheduleId': '排班 ID',
    'scheduleList.default': '預設',
    'scheduleList.defaultScheduleNoDateRange': '預設排班 (無日期限制)',
    'scheduleList.validPeriod': '有效期限',
    'scheduleList.to': '至',
    'scheduleList.noSchedule': '無排班',

    // Doctor schedules JSON bulk export/import
    'doctorSchedulesJson.title': 'JSON 匯出 / 一次設定全部',
    'doctorSchedulesJson.hide': '收起',
    'doctorSchedulesJson.show': '展開',
    'doctorSchedulesJson.export': '匯出 JSON',
    'doctorSchedulesJson.upload': '上傳 JSON',
    'doctorSchedulesJson.replaceListed': '取代所列醫生',
    'doctorSchedulesJson.upsert': '依 ID 更新',
    'doctorSchedulesJson.import': '匯入 / 設定',
    'doctorSchedulesJson.exportOk': '已匯出醫生排班 JSON。',
    'doctorSchedulesJson.exportFail': '匯出失敗',
    'doctorSchedulesJson.invalidJson': '無效的 JSON 檔案。',
    'doctorSchedulesJson.needArray': 'JSON 必須包含非空的 doctor_schedules 陣列。',
    'doctorSchedulesJson.confirmImport': '確定要從 JSON 匯入醫生排班嗎？',
    'doctorSchedulesJson.importOk': '匯入完成。',
    'doctorSchedulesJson.importFail': '匯入失敗',

    // Block Schedule Dialog
    'blockScheduleDialog.editBlockSchedule': '編輯假期',
    'blockScheduleDialog.addBlockSchedule': '新增假期',
    'blockScheduleDialog.selectDoctor': '選擇醫生',
    'blockScheduleDialog.allDoctors': '所有醫生',
    'blockScheduleDialog.reason': '原因',
    'blockScheduleDialog.reasonPlaceholder': '輸入原因 (例如: 公眾假期、休假)',
    'blockScheduleDialog.blockFrom': '開始',
    'blockScheduleDialog.blockUntil': '結束',
    'blockScheduleDialog.quickSelect': '快速選擇',
    'blockScheduleDialog.today': '今天',
    'blockScheduleDialog.tomorrow': '明天',
    'blockScheduleDialog.thisWeek': '本週',
    'blockScheduleDialog.nextWeek': '下週',
    'blockScheduleDialog.updateBlockSchedule': '更新假期',
    'blockScheduleDialog.createBlockSchedule': '創建假期',

    // Block Schedule List (Holiday)
    'blockScheduleList.noBlockSchedules': '尚未建立假期。請點擊「新增假期」創建。',
    'blockScheduleList.establishedBlockSchedules': '假期',
    'blockScheduleList.blockScheduleId': 'ID',
    'blockScheduleList.inactive': '非活躍',
    'blockScheduleList.active': '活躍',
    'blockScheduleList.upcoming': '即將到來',
    'blockScheduleList.expired': '已過期',
    'blockScheduleList.reason': '原因',
    'blockScheduleList.duration': '持續時間',
    'blockScheduleList.blockFrom': '開始',
    'blockScheduleList.blockUntil': '結束',
    'blockScheduleList.affectedDoctor': '受影響醫生',
    'blockScheduleList.scope': '範圍',
    'blockScheduleList.allDoctors': '所有醫生',
    'blockScheduleList.createdAt': '創建時間',
    'blockScheduleList.updatedAt': '更新時間',

    // Meta WhatsApp Templates
    'metaTemplates.title': 'Meta WhatsApp 模板',
    'metaTemplates.description': '創建和管理用於營銷和實用消息的 WhatsApp 消息模板',
    'metaTemplates.createTemplate': '創建模板',
    'metaTemplates.allStatuses': '所有狀態',
    'metaTemplates.approved': '已批准',
    'metaTemplates.pending': '待審核',
    'metaTemplates.rejected': '已拒絕',
    'metaTemplates.name': '模板名稱',
    'metaTemplates.category': '類別',
    'metaTemplates.language': '語言',
    'metaTemplates.status': '狀態',
    'metaTemplates.variables': '變量',
    'metaTemplates.header': '標題',
    'metaTemplates.body': '正文',
    'metaTemplates.footer': '頁腳',
    'metaTemplates.buttons': '按鈕',
    'metaTemplates.templateName': '模板名稱',
    'metaTemplates.descriptionField': '描述',
    'metaTemplates.downloadCSV': '下載 CSV 模板',
    'metaTemplates.sendBroadcast': '發送廣播',
    'metaTemplates.downloadCSVTemplate': '下載 CSV 模板',
    'metaTemplates.uploadCSV': '上傳 CSV 文件',
    'metaTemplates.selectCSVFile': '點擊選擇 CSV 文件',
    'metaTemplates.variableExamples': '變量示例',
    'metaTemplates.variableExamplesNote': '為每個變量提供示例值。這些示例將顯示給 Meta 審核人員。',
    'metaTemplates.typeOfVariable': '變量類型',
    'metaTemplates.variableTypeName': '名稱',
    'metaTemplates.variableTypeNumber': '數字',
    'metaTemplates.templateDetails': '模板詳情',
    'metaTemplates.templateId': '模板 ID',
    'metaTemplates.templateComponents': '模板組件',
    'metaTemplates.rawTemplateData': '原始模板數據',
    'metaTemplates.noTemplateData': '無模板數據',
    'metaTemplates.templateInfoNotLoaded': '無法加載模板信息。',
    'metaTemplates.parameterFormat': '參數格式',
    'metaTemplates.positionalFormat': '位置格式 ({{1}}, {{2}}, ...)',
    'metaTemplates.namedFormat': '命名格式 ({{variable_name}})',
    'metaTemplates.positionalFormatHelp': '變量將按順序轉換為 {{1}}, {{2}} 等。',
    'metaTemplates.namedFormatHelp': '變量將保留其名稱：{{variable_name}}',
    'metaTemplates.variableHintPositional': '使用 {{variable_name}} 添加變量。它們將被轉換為 {{1}}, {{2}} 等。',
    'metaTemplates.variableHintNamed': '使用 {{variable_name}} 添加變量。變量名稱必須是小寫字母和下劃線。',
    'metaTemplates.resend': '重新發送模板',
    'metaTemplates.confirmResend': '您確定要將此模板重新發送到 Meta 進行審核嗎？',
    'metaTemplates.resendSuccess': '模板重新發送成功！',
    'metaTemplates.resendError': '重新發送模板失敗',
    'common.refresh': '刷新',
    'common.loading': '載入中...',
    'common.close': '關閉',
    'common.submitting': '提交中...',
    'common.submit': '提交審核',
    'common.sending': '發送中...',

    // Embedded Signup
    'embeddedSignup.title': 'Meta 嵌入式接入',
    'embeddedSignup.description': '使用此頁面透過 Meta 嵌入式註冊流程連接既有的 WhatsApp Business 帳號。',
    'embeddedSignup.button.label': '啟動嵌入式接入流程',
    'embeddedSignup.button.loading': '準備中...',
    'embeddedSignup.button.caption': '此動作會在新視窗開啟 Meta 的嵌入式接入流程。',
    'embeddedSignup.placeholderSuccess': '已啟動嵌入式接入流程（占位訊息）。',
    'embeddedSignup.placeholderError': '目前無法啟動嵌入式接入，請稍後再試。',
    'embeddedSignup.status.sdkTitle': 'Facebook SDK',
    'embeddedSignup.status.sdkReady': 'Facebook SDK 已就緒。',
    'embeddedSignup.status.sdkLoading': '正在等待 Facebook SDK 載入完成...',
    'embeddedSignup.status.codeTitle': '授權碼',
    'embeddedSignup.status.codeIdle': '尚未偵測到授權碼。',
    'embeddedSignup.status.codeProcessing': '正在處理授權碼...',
    'embeddedSignup.status.codeSuccess': '授權碼交換成功。',
    'embeddedSignup.status.codeError': '授權碼交換失敗。',
    'embeddedSignup.status.latestResult': '最新結果',
    'embeddedSignup.status.wabaLabel': 'WABA ID：',
    'embeddedSignup.status.expiresLabel': '權杖到期時間：',
    'embeddedSignup.status.noResult': '尚未產生接入結果。',
    'embeddedSignup.status.sessionTitle': '會話事件',
    'embeddedSignup.status.sessionEvent': '事件：{{event}}',
    'embeddedSignup.status.sessionTimestamp': '接收時間：{{value}}',
    'embeddedSignup.status.sessionNone': '尚未捕捉到會話事件。',
    'embeddedSignup.success.exchange': 'WhatsApp 接入完成。',
    'embeddedSignup.error.exchange': '無法完成 WhatsApp 接入。',
    'embeddedSignup.error.sdkNotReady': 'Facebook SDK 仍在載入，請稍候再試。',
    'embeddedSignup.error.httpsRequired': 'Facebook 登入需要 HTTPS（或 localhost）。請使用 HTTPS 網域，例如您的 ngrok 網址。',
    'embeddedSignup.error.missingConfig': '缺少嵌入式接入設定。請設定 Meta App ID 與 Config ID。',
    'embeddedSignup.error.fbCancelled': 'Meta 登入已被取消。',
    'embeddedSignup.accessRestricted': '訪問受限',
    'embeddedSignup.accessRestrictedMessage': '此功能僅供授權診所使用。',
    'embeddedSignup.instruction.step1Title': '1. 先發送訊息：',
    'embeddedSignup.instruction.step1Text': '請從您的手機（訂閱管理頁面中的號碼）發送任意訊息（例如「你好」）至您要連接的商業 WhatsApp 號碼。',
    'embeddedSignup.instruction.step2Title': '2. 開始接入流程：',
    'embeddedSignup.instruction.step2Text': '點擊下方「啟動嵌入式接入流程」按鈕，並按照畫面指示完成 Meta 授權。',
    'embeddedSignup.instruction.step3Title': '3. 接收確認訊息：',
    'embeddedSignup.instruction.step3Text': '設定完成後，您將在手機上收到確認訊息。',

    // Token onboarding
    'onboarding.title': '診所上線流程',
    'onboarding.loading': '正在載入上線流程...',
    'onboarding.invalidLink.title': '無效的上線連結',
    'onboarding.stepCounter': '第 {{current}} 步，共 {{total}} 步',
    'onboarding.clinicName': '診所名稱：',
    'onboarding.region': '地區：',
    'onboarding.continue': '繼續',
    'onboarding.saving': '儲存中...',
    'onboarding.creating': '建立中...',
    'onboarding.submitting': '提交中...',
    'onboarding.confirmClinic.title': '確認診所資料',
    'onboarding.confirmClinic.button': '確認並繼續',
    'onboarding.email.title': '提供管理員電郵',
    'onboarding.password.title': '設定管理員密碼',
    'onboarding.password.button': '建立管理員並繼續',
    'onboarding.embedded.title': '連接 WhatsApp（嵌入式接入）',
    'onboarding.embedded.description': '使用 Meta 嵌入式接入流程將 WhatsApp 號碼連接到 Nexi。',
    'onboarding.embedded.start': '開始嵌入式接入',
    'onboarding.embedded.connected': '已連接',
    'onboarding.questionnaire.title': '請介紹你的業務',
    'onboarding.questionnaire.complete': '完成上線流程',
    'onboarding.completed.title': '上線流程已完成',
    'onboarding.completed.description': '你的診所設定已完成，現在可以開始使用 Nexi。',
    'onboarding.completed.success': '上線流程已成功完成。',
    'onboarding.error.missingToken': '缺少上線 token。',
    'onboarding.error.invalidToken': '上線 token 無效或已過期。',
    'onboarding.error.invalidEmail': '請輸入有效的電郵地址。',
    'onboarding.error.passwordLength': '密碼至少需要 6 個字元。',
    'onboarding.error.sdkNotReady': 'Facebook SDK 尚未準備好。',
    'onboarding.error.embeddedCancelled': '嵌入式接入已取消或沒有返回授權碼。',
    'onboarding.error.requiredFields': '請填寫所有必填欄位。',
    'onboarding.error.disclosePrice': '請選擇是否公開價格。',
    'onboarding.error.serviceCost': '請提供服務收費資訊。',
    'onboarding.error.aiConsent': '請選擇 AI 訓練授權。',
    'onboarding.admin.created': '管理員帳號已建立。',

    // 預約提醒設定
    'appointmentReminderSettings.title': '預約提醒設定',
    'appointmentReminderSettings.subtitle': '管理診所的預約提醒設定',
    'appointmentReminderSettings.addNew': '新增設定',
    'appointmentReminderSettings.filters': '篩選',
    'appointmentReminderSettings.location': '位置',
    'appointmentReminderSettings.doctor': '醫生',
    'appointmentReminderSettings.enabledStatus': '啟用狀態',
    'appointmentReminderSettings.allLocations': '所有位置',
    'appointmentReminderSettings.allDoctors': '所有醫生',
    'appointmentReminderSettings.all': '全部',
    'appointmentReminderSettings.enabled': '已啟用',
    'appointmentReminderSettings.disabled': '已停用',
    'appointmentReminderSettings.clearFilters': '清除篩選',
    'appointmentReminderSettings.editSetting': '編輯設定',
    'appointmentReminderSettings.addNewSetting': '新增設定',
    'appointmentReminderSettings.timezone': '時區',
    'appointmentReminderSettings.allLocationsNoSpecific': '所有位置（無特定位置）',
    'appointmentReminderSettings.allDoctorsNoSpecific': '所有醫生（無特定醫生）',
    'appointmentReminderSettings.reminderDaysBefore': '提醒天數',
    'appointmentReminderSettings.reminderDaysBeforeHint': '預約前多少天發送提醒',
    'appointmentReminderSettings.cutoffReminderAt': '提醒截止時間',
    'appointmentReminderSettings.cutoffReminderAtHint': '發送提醒的截止時間（HH:MM 格式）',
    'appointmentReminderSettings.setting': '設定',
    'appointmentReminderSettings.disable': '停用',
    'appointmentReminderSettings.enable': '啟用',
    'appointmentReminderSettings.daysBefore': '天數',
    'appointmentReminderSettings.cutoffTime': '截止時間',
    'appointmentReminderSettings.noSettings': '未找到預約提醒設定。',
    'appointmentReminderSettings.error.loadFailed': '載入預約提醒設定失敗',
    'appointmentReminderSettings.error.saveFailed': '儲存預約提醒設定失敗',
    'appointmentReminderSettings.error.deleteFailed': '刪除預約提醒設定失敗',
    'appointmentReminderSettings.error.toggleFailed': '切換預約提醒設定狀態失敗',
    'appointmentReminderSettings.error.invalidTimeFormat': '提醒截止時間必須為 HH:MM 格式（例如：17:00）',
    'appointmentReminderSettings.success.created': '預約提醒設定建立成功',
    'appointmentReminderSettings.success.updated': '預約提醒設定更新成功',
    'appointmentReminderSettings.success.deleted': '預約提醒設定刪除成功',
    'appointmentReminderSettings.success.toggled': '預約提醒設定狀態切換成功',
    'appointmentReminderSettings.confirm.delete': '您確定要刪除此預約提醒設定嗎？',
    'appointmentReminderSettings.notAvailable': '不適用',

    // 數據導入
    'import.andMore': '... 還有 {{count}} 項'
  }
};

export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    // 從localStorage獲取保存的語言，默認為繁體中文
    return localStorage.getItem('language') || 'zh_HK';
  });

  // 當語言改變時保存到localStorage
  useEffect(() => {
    localStorage.setItem('language', currentLanguage);
  }, [currentLanguage]);

  // 翻譯函數
  const t = (key, defaultValue = key) => {
    return translations[currentLanguage]?.[key] || defaultValue;
  };

  // 切換語言
  const changeLanguage = (languageCode) => {
    if (SUPPORTED_LANGUAGES[languageCode]) {
      setCurrentLanguage(languageCode);
    }
  };

  // 獲取當前語言信息
  const getCurrentLanguageInfo = () => {
    return SUPPORTED_LANGUAGES[currentLanguage];
  };

  const value = {
    currentLanguage,
    changeLanguage,
    t,
    getCurrentLanguageInfo,
    supportedLanguages: SUPPORTED_LANGUAGES
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
